import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';
import { BuilderComponentType, BuilderJsonOutput, BuilderNode, BuilderProps, OutputField, OutputPanel, OutputTab, TabsProps, PanelProps, TextAreaProps, TextProps, TabItemProps, NumberProps, SwitchProps, SimpleTableProps, TableProps, TableConfiguration, DateProps, SelectProps, DropdownProps, QuerySelectProps, KeyValueProps, KeyValueObjectProps, TagProps, SearchProps, DividerProps, SectionProps, ButtonProps, DataGridProps, RowProps, ImageProps } from '../interfaces/builder-screen.models';
import { ScreenTemplate, TemplateComponent } from '../interfaces/template.models';
import { JsonValidationService } from './json-validation.service';
import { WebhookConfiguration, WebhookTab, WebhookPanel, WebhookField } from '../../webhook-creator/webhook-creator/webhook-interfaces/configuration';
import { TabControlService } from './tab-control.service';
import { BuilderValidationService } from './builder-validation.service';
import { NzMessageService } from 'ng-zorro-antd/message';

export interface BuilderScreenV2State {
  nodes: ReadonlyArray<BuilderNode>;
  selectedId: string | null;
}

// Interfaces para JSON Preview Fix
export interface JsonSyncConfiguration {
  autoApply: boolean;
  debounceTime: number;
  validateSchema: boolean;
  preserveSelection: boolean;
}

export interface JsonApplyResult {
  success: boolean;
  errors: JsonValidationError[];
  warnings: string[];
  appliedNodes: number;
}

export interface JsonValidationError {
  type: 'syntax' | 'schema' | 'component';
  message: string;
  line?: number;
  column?: number;
  componentType?: string;
}

export interface JsonValidationResult {
  isValid: boolean;
  errors: JsonValidationError[];
  warnings?: string[];
}

@Injectable()
export class BuilderScreenBetaService {
  private readonly stateSubject = new BehaviorSubject<BuilderScreenV2State>({ nodes: [], selectedId: null });
  public readonly state$: Observable<BuilderScreenV2State> = this.stateSubject.asObservable();
  
  // Flag para pular validação ao carregar templates
  private isLoadingTemplate = false;

  constructor(
    private jsonValidationService: JsonValidationService,
    private tabControlService: TabControlService,
    private validationService: BuilderValidationService,
    private message: NzMessageService
  ) {
    // Configurar referência para evitar dependência circular
    this.tabControlService.setBuilderService(this);
  }

  // Getter público para acessar o state (necessário para o canvas webhook)
  get currentState() {
    return this.stateSubject.getValue();
  }

  /**
   * Retorna o id do panel que contém o node (sobe parentId até achar um panel).
   */
  getContainingPanelId(nodeId: string): string | null {
    const nodes = this.currentState.nodes;
    let current = nodes.find(n => n.id === nodeId);
    while (current?.parentId) {
      const parent = nodes.find(n => n.id === current!.parentId);
      if (!parent) return null;
      if (parent.type === 'panel') return parent.id;
      current = parent;
    }
    return null;
  }

  /**
   * Retorna os ids de todos os nós que são descendentes do panel (filhos diretos ou dentro de row, etc.).
   * Usado para incluir campos que estão dentro de componente de linha (row) no preenchimento do formulário.
   */
  getNodeIdsUnderPanel(nodes: ReadonlyArray<BuilderNode>, panelId: string): Set<string> {
    const ids = new Set<string>();
    const collect = (parentId: string) => {
      nodes.forEach(n => {
        if (n.parentId === parentId) {
          ids.add(n.id);
          collect(n.id);
        }
      });
    };
    collect(panelId);
    return ids;
  }

  /**
   * Retorna o dataPath da grade cujo formulário está no panel (masterDetailFormPanel ou masterDetailFormPanels).
   * @param panelId ID do panel
   * @param preferArrayPath Se o panel for usado por mais de uma grade (pai e filha), preferir a grade com este path (ex: mainSelection.arrayPath da grade pai).
   */
  getGridDataPathForPanel(panelId: string, preferArrayPath?: string): string | undefined {
    const nodes = this.currentState.nodes;
    
    // CORREÇÃO (Regra 11): Verificar se há uma grade DENTRO do painel (parentId === panelId).
    // Grades fisicamente no painel têm prioridade para escrita pois o campo de formulário
    // e a grade estão no MESMO contexto (mesma aba filha).
    // Sem isso, getGridDataPathForPanel retorna o dataPath da grade PRINCIPAL quando ela
    // lista o painel da aba filha em masterDetailFormPanels, fazendo a escrita ir para
    // o item pai em vez do item filho (ex: response.magalu[0].extras.myName em vez de
    // response.magalu[0].channels[0].extras.myName).
    const gridsInPanel = nodes.filter(n => {
      if (n.type !== 'datagrid') return false;
      return n.parentId === panelId;
    });
    
    if (gridsInPanel.length === 1) {
      return (gridsInPanel[0].props as DataGridProps)?.dataPath;
    }
    
    if (gridsInPanel.length > 1) {
      // Múltiplas grades no mesmo painel: usar preferArrayPath para desambiguar
      if (preferArrayPath) {
        const exact = gridsInPanel.find(n => (n.props as DataGridProps)?.dataPath === preferArrayPath);
        if (exact) return (exact.props as DataGridProps)?.dataPath;
      }
      // Fallback: path mais longo (mais específico/aninhado)
      const sorted = [...gridsInPanel].sort((a, b) => {
        const pathA = (a.props as DataGridProps)?.dataPath || '';
        const pathB = (b.props as DataGridProps)?.dataPath || '';
        return pathB.length - pathA.length;
      });
      return (sorted[0].props as DataGridProps)?.dataPath;
    }
    
    // Nenhuma grade fisicamente no painel: usar lógica existente (masterDetailFormPanel/masterDetailFormPanels)
    const gridsWithPanel = nodes.filter(n => {
      if (n.type !== 'datagrid') return false;
      const props = n.props as DataGridProps;
      return props?.masterDetailFormPanel === panelId ||
        (props?.masterDetailFormPanels && props.masterDetailFormPanels.includes(panelId));
    });
    if (gridsWithPanel.length === 0) return undefined;
    
    // CORREÇÃO: Se há múltiplas grades usando o mesmo painel, preferir a grade PAI (path mais curto)
    // Isso resolve o problema quando grade pai e filha compartilham o mesmo painel de formulário
    if (gridsWithPanel.length > 1) {
      // Ordenar por tamanho do path (menor = mais provável ser a grade pai)
      const sorted = [...gridsWithPanel].sort((a, b) => {
        const pathA = (a.props as DataGridProps)?.dataPath || '';
        const pathB = (b.props as DataGridProps)?.dataPath || '';
        return pathA.length - pathB.length;
      });
      
      // Se o preferArrayPath corresponde exatamente a uma grade, usar ela
      if (preferArrayPath) {
        const exact = sorted.find(n => (n.props as DataGridProps)?.dataPath === preferArrayPath);
        if (exact) return (exact.props as DataGridProps).dataPath;
        
        // Se preferArrayPath é filho de alguma grade, usar a grade pai
        const parentGrid = sorted.find(n => {
          const gridPath = (n.props as DataGridProps)?.dataPath;
          return gridPath && preferArrayPath.startsWith(gridPath + '.');
        });
        if (parentGrid) return (parentGrid.props as DataGridProps).dataPath;
      }
      
      // Fallback: retornar a grade com path mais curto (provavelmente a grade pai)
      return (sorted[0].props as DataGridProps)?.dataPath;
    }
    
    if (preferArrayPath) {
      const preferred = gridsWithPanel.find(n => (n.props as DataGridProps)?.dataPath === preferArrayPath);
      if (preferred) return (preferred.props as DataGridProps).dataPath;
    }
    return (gridsWithPanel[0].props as DataGridProps)?.dataPath;
  }

  public readonly nodes$: Observable<ReadonlyArray<BuilderNode>> = this.state$.pipe(map(s => s.nodes));
  public readonly selectedId$: Observable<string | null> = this.state$.pipe(map(s => s.selectedId));
  public readonly selectedNode$: Observable<BuilderNode | null> = combineLatest([this.nodes$, this.selectedId$]).pipe(
    map(([nodes, id]) => nodes.find(n => n.id === id) ?? null)
  );
  public readonly jsonOutput$: Observable<BuilderJsonOutput | WebhookConfiguration> = this.nodes$.pipe(map(nodes => this.mapToJson(nodes)));
  public readonly jsonText$: Observable<string> = this.jsonOutput$.pipe(map(j => JSON.stringify(j, null, 2)));

  // Rastreia aba ativa por id do nó de abas
  private readonly activeTabByTabsId: Record<string, string> = {};

  /**
   * Aplica um template ao canvas, criando todos os componentes configurados
   * ✅ CORRIGIDO: Agora verifica se o template tem estrutura JSON completa (tabs/panels/fields/rows)
   */
  applyTemplate(template: ScreenTemplate): void {
    try {
      

      // ✅ NOVA LÓGICA: Se o template tem JSON completo, usar loadFromJson
      if ((template as any).tabs && (template as any).panels && (template as any).fields) {
      
        const jsonConfig: any = {
          tabs: (template as any).tabs,
          panels: (template as any).panels,
          fields: (template as any).fields,
          rows: (template as any).rows || [] //  NOVO: Incluir array de Rows
        };
        // Barra de Contexto: incluir contextFields se existir
        if ((template as any).contextFields) {
          jsonConfig.contextFields = (template as any).contextFields;
        }
        this.loadFromJson(jsonConfig);
        return;
      }

      // ✅ LÓGICA ANTIGA: Se o template só tem "components" (formato antigo), criar estrutura básica

      // Valida o template antes de aplicar
      if (!template || !template.components || template.components.length === 0) {
        throw new Error('Template inválido ou sem componentes');
      }

      const current = this.stateSubject.getValue();
      
      // Limpa o canvas atual
      const clearedState = { nodes: [], selectedId: null };
      
      // Cria um container de abas para o template
      const tabsNode: BuilderNode = {
        id: this.generateId('tabs'),
        type: 'tabs',
        props: {
                  items: [{
          id: this.generateId('tab'),
          name: template.name,
          icon: 'appstore',
          themeIcon: 'twotone',
          twotoneColor: '#1890ff',
          disabled: false
        }]
        } as TabsProps,
        children: [],
        parentId: null,
        tabId: null,
      };

      // Cria todos os componentes do template
      const templateNodes: BuilderNode[] = template.components.map((component, index) => {
        try {
          const node: BuilderNode = {
            id: this.generateId(component.type),
            type: component.type,
            props: this.mapTemplatePropsToBuilderProps(component),
            children: undefined,
            parentId: tabsNode.id,
            tabId: (tabsNode.props as TabsProps).items[0].id,
          };
          return node;
        } catch (componentError) {
          console.error(`Erro ao criar componente ${index + 1}:`, componentError);
          throw new Error(`Falha ao criar componente ${index + 1}: ${componentError}`);
        }
      });

      // Define a aba ativa
      this.activeTabByTabsId[tabsNode.id] = (tabsNode.props as TabsProps).items[0].id;

      // Atualiza o estado
      const nextNodes = [tabsNode, ...templateNodes];
      this.stateSubject.next({ nodes: nextNodes, selectedId: tabsNode.id });
      
    } catch (error) {
      console.error('Erro ao aplicar template:', error);
      // Reverte mudanças em caso de erro
      this.stateSubject.next({ nodes: [], selectedId: null });
      throw error;
    }
  }

  /**
   * Mapeia as propriedades do template para as propriedades do builder
   */
  private mapTemplatePropsToBuilderProps(templateComponent: TemplateComponent): BuilderProps {
    const baseProps = templateComponent.props;
    
    switch (templateComponent.type) {
      case 'text':
        return {
          label: baseProps.label,
          placeholder: baseProps.placeholder,
          required: baseProps.required,
          rowSpan: baseProps.rowSpan,
          colSpan: baseProps.colSpan,
        } as TextProps;
        
      case 'textarea':
        return {
          label: baseProps.label,
          placeholder: baseProps.placeholder,
          required: baseProps.required,
          rowSpan: baseProps.rowSpan,
          colSpan: baseProps.colSpan,
        } as TextAreaProps;
        
      case 'number':
        return {
          label: baseProps.label,
          placeholder: baseProps.placeholder,
          required: baseProps.required,
          rowSpan: baseProps.rowSpan,
          colSpan: baseProps.colSpan,
        } as NumberProps;
        
      case 'switch':
        return {
          label: baseProps.label,
          checkedChildren: baseProps.checkedChildren,
          unCheckedChildren: baseProps.unCheckedChildren,
          rowSpan: baseProps.rowSpan,
          colSpan: baseProps.colSpan,
        } as SwitchProps;
        
      case 'button':
        return {
          label: baseProps.label,
          rowSpan: baseProps.rowSpan,
          colSpan: baseProps.colSpan,
          text: (baseProps as any).text,
          buttonType: (baseProps as any).buttonType,
          size: (baseProps as any).size,
          disabled: (baseProps as any).disabled,
          loading: (baseProps as any).loading,
          icon: (baseProps as any).icon,
          backgroundColor: (baseProps as any).backgroundColor,
          textColor: (baseProps as any).textColor,
          actionType: (baseProps as any).actionType,
          flowConfig: (baseProps as any).flowConfig,
        } as ButtonProps;
        
      default:
        return baseProps as BuilderProps;
    }
  }

  addNode(type: BuilderComponentType): void {
    const current = this.stateSubject.getValue();
    const selected = current.nodes.find(n => n.id === current.selectedId) || null;

    // Pular validação se estiver carregando template
    if (!this.isLoadingTemplate) {
      // 1. VALIDAÇÃO: Verificar se precisa criar estrutura base automaticamente
      if (!this.validationService.hasBaseStructure(current.nodes) && 
          type !== 'tabs' && type !== 'panel') {
        // Canvas vazio ou sem estrutura base - criar automaticamente        
        this.createBaseStructure();
        // Após criar, adicionar o componente no panel criado (com delay para dar tempo do state atualizar)
        setTimeout(() => this.addNode(type), 100);
        return;
      }

      // 2. VALIDAÇÃO: Verificar hierarquia correta
      const validation = this.validationService.validateComponentPlacement(
        type, 
        selected, 
        current.nodes
      );

      if (!validation.isValid) {
        this.showValidationError(validation);
        return; // BLOQUEAR adição
      }
    }

    // Comportamento especial: adicionar 'tabs' anexa uma nova aba a um container de abas existente quando possível
    if (type === 'tabs') {
      // Prefere abas selecionadas, depois abas pai da seleção, depois primeiras abas no canvas
      const candidateFromParent = (selected && selected.parentId) ? (current.nodes.find(n => n.id === selected.parentId && n.type === 'tabs') || null) : null;
      const candidateFromCanvas = current.nodes.find(n => n.type === 'tabs') || null;
      const targetTabs = (selected?.type === 'tabs') ? selected : (candidateFromParent ?? candidateFromCanvas);

      if (targetTabs) {
        const updatedTabs = this.appendTabItem(targetTabs);
        const nextNodes = current.nodes.map(n => n.id === updatedTabs.id ? updatedTabs : n);
        this.stateSubject.next({ nodes: nextNodes, selectedId: updatedTabs.id });
        return;
      }
      // fallthrough para criar o primeiro container de abas se nenhum existir
    }

    const node: BuilderNode = {
      id: this.generateId(type),
      type,
      props: this.createDefaultProps(type) as BuilderProps,
      children: type === 'tabs' ? [] : undefined,
      parentId: null,
      tabId: null,
    };

    //  NOVO: Calcular order automático para TODOS os componentes (não só Panel e Row)
    // Determinar qual tabId e parentId serão usados
    let targetTabId: string | null = null;
    let targetParentId: string | null = null;
    
    if (selected && selected.type === 'tabs') {
      const items = (selected.props as TabsProps).items || [];
      targetTabId = this.activeTabByTabsId[selected.id] ?? items[0]?.id ?? null;
      targetParentId = null; // Filho direto da tab
    } else if (selected && selected.type === 'panel') {
      targetTabId = selected.tabId;
      targetParentId = selected.id; // Filho do panel
    } else if (selected && selected.type === 'row') {
      targetTabId = selected.tabId;
      targetParentId = selected.id; // Filho do row
    } else if (selected && selected.tabId) {
      targetTabId = selected.tabId;
      targetParentId = selected.parentId; // Herdar do irmão selecionado
    }

    // Calcular próximo order disponível nesse contexto (mesma tab, mesmo parent)
    const siblings = current.nodes.filter(n => {
      if (n.tabId !== targetTabId || n.parentId !== targetParentId) return false;
      // Se for filho direto da tab, Panels têm ordenação própria
      if (!targetParentId && type !== 'panel' && n.type === 'panel') return false;
      if (!targetParentId && type === 'panel' && n.type !== 'panel') return false;
      return true;
    });
    
    const maxOrder = siblings.length > 0
      ? Math.max(...siblings.map(n => (n.props as any)?.order ?? 0))
      : -1;
    (node.props as any).order = maxOrder + 1;

    // NOVA FUNCIONALIDADE: Se um Panel está selecionado, adiciona como filho do Panel
    if (selected && selected.type === 'panel') {
      // Verificar se é um field válido para Panel (não containers)
      if (['tabs', 'panel'].includes(type)) {
        return;
      }
      node.parentId = selected.id;
      node.tabId = selected.tabId; // Herdar tabId do panel pai
    }
    //  NOVO: Se um Row está selecionado, adiciona como filho do Row (lado a lado)
    else if (selected && selected.type === 'row') {
      // Verificar se é um field válido para Row (não containers grandes)
      if (['tabs', 'panel', 'row'].includes(type)) {
        return;
      }
      node.parentId = selected.id;
      node.tabId = selected.tabId; // Herdar tabId do row pai
      // Campos dentro de Row começam com colSpan menor para facilitar layout lado a lado
      if ((node.props as any).colSpan === 24) {
        (node.props as any).colSpan = 12; // Padrão 50% para campos dentro de Row
      }
    }
    // COMPORTAMENTO EXISTENTE: Se um container de aba está selecionado, anexa novo nó à sua aba ativa
    else if (selected && selected.type === 'tabs') {
      node.parentId = selected.id;
      const items = (selected.props as TabsProps).items || [];
      const active = this.activeTabByTabsId[selected.id] ?? items[0]?.id ?? null;
      node.tabId = active;
    }
    // COMPORTAMENTO EXISTENTE: Se um filho sob um container de abas está selecionado, herda seu pai e aba
    else if (selected && selected.parentId && selected.tabId) {
      node.parentId = selected.parentId;
      node.tabId = selected.tabId;
    }

    const nextNodes = [...current.nodes, node];
    
    // MELHORIA UX: Manter seleção no Panel/Row pai quando adicionamos campos como filhos
    const shouldKeepContainerSelected = selected && 
      (selected.type === 'panel' || selected.type === 'row') && 
      node.parentId === selected.id;
    
    const newSelectedId = shouldKeepContainerSelected
      ? selected.id  // Manter container selecionado para facilitar adição de mais campos
      : node.id;     // Comportamento padrão para outros casos
    
    this.stateSubject.next({ nodes: nextNodes, selectedId: newSelectedId });
  }

  setActiveTab(tabsNodeId: string, tabId: string): void {
    this.activeTabByTabsId[tabsNodeId] = tabId;
  }

  getActiveTabId(tabsNodeId: string, items?: ReadonlyArray<TabItemProps>): string | null {
    const stored = this.activeTabByTabsId[tabsNodeId];
    if (stored) return stored;
    const first = items && items.length > 0 ? items[0].id : null;
    return first ?? null;
  }

  private appendTabItem(tabsNode: BuilderNode): BuilderNode {
    const props = (tabsNode.props as TabsProps) || { items: [] } as TabsProps;
    const newItem: TabItemProps = {
      id: this.generateId('tab'),
      name: 'builderScreen.tab.general',
      icon: 'setting',
      themeIcon: 'twotone',
      twotoneColor: '#52c41a',
      disabled: false
    };
    const next: BuilderNode = {
      ...tabsNode,
      props: { ...(props as any), items: [...(props.items || []), newItem] } as TabsProps
    };
    this.activeTabByTabsId[tabsNode.id] = newItem.id;
    return next;
  }

  /**
   * Cria estrutura base Tab + Panel automaticamente
   * Usado quando canvas está vazio e usuário tenta adicionar um componente
   */
  private createBaseStructure(): void {
    const tabId = this.generateId('tab');
    
    // Criar Tab
    const tabNode: BuilderNode = {
      id: this.generateId('tabs'),
      type: 'tabs',
      props: {
        items: [{ 
          id: tabId, 
          name: 'builderScreen.tab.main',
          icon: 'folder',
          themeIcon: 'outline',
          twotoneColor: '#1890ff',
          disabled: false 
        }],
        colSpan: 24,
        rowSpan: 1
      } as TabsProps,
      children: [],
      parentId: null,
      tabId: null
    };

    // Criar Panel dentro da Tab
    const panelNode: BuilderNode = {
      id: this.generateId('panel'),
      type: 'panel',
      props: {
        name: 'builderScreen.panel.main',
        active: true,
        collapsible: false,
        collapsed: false,
        order: 1,
        colSpan: 24,
        rowSpan: 1
      } as PanelProps,
      parentId: tabNode.id,
      tabId: tabId
    };

    // Registrar tab ativa
    this.activeTabByTabsId[tabNode.id] = tabId;

    // Atualizar estado
    const nextNodes = [tabNode, panelNode];
    this.stateSubject.next({ 
      nodes: nextNodes, 
      selectedId: panelNode.id // Selecionar panel automaticamente
    });
    
    this.message.success('Estrutura base criada automaticamente! Adicione seus componentes dentro do painel.');
  }

  /**
   * Exibe erro de validação ao usuário
   */
  private showValidationError(validation: import('../interfaces/builder-screen.models').ValidationResult): void {
    const { errorMessage, suggestedAction } = validation;
    
    this.message.error(
      `❌ ${errorMessage}\n\n💡 ${suggestedAction}`,
      { nzDuration: 5000 }
    );
  }

  updateTabsItemName(tabsNodeId: string, tabId: string, name: string): void {
    const current = this.stateSubject.getValue();
    const nextNodes = current.nodes.map(node => {
      if (node.id !== tabsNodeId || node.type !== 'tabs') return node;
      const props = (node.props as TabsProps) || { items: [] } as TabsProps;
      const nextItems = (props.items || []).map(it => it.id === tabId ? { ...it, name } : it);
      return { ...node, props: { ...(props as any), items: nextItems } as TabsProps } as BuilderNode;
    });
    this.stateSubject.next({ ...current, nodes: nextNodes });
  }

  updateTabsItemIcon(tabsNodeId: string, tabId: string, icon: string): void {
    const current = this.stateSubject.getValue();
    const nextNodes = current.nodes.map(node => {
      if (node.id !== tabsNodeId || node.type !== 'tabs') return node;
      const props = (node.props as TabsProps) || { items: [] } as TabsProps;
      const nextItems = (props.items || []).map(item => 
        item.id === tabId ? { ...item, icon: icon || undefined } : item
      );
      return { ...node, props: { ...(props as any), items: nextItems } as TabsProps } as BuilderNode;
    });
    this.stateSubject.next({ ...current, nodes: nextNodes });
  }

  // Remove um único nó (campo ou container de abas) e cascateia remoção dos filhos
  removeNode(nodeId: string): void {
    const current = this.stateSubject.getValue();
    const nodeToRemove = current.nodes.find(n => n.id === nodeId);
    
    if (!nodeToRemove) return; // Nó não encontrado
    
    // Se removendo um container de abas, também remove todos os seus campos filhos
    let nextNodes = current.nodes.filter(n => n.id !== nodeId);
    if (nodeToRemove.type === 'tabs') {
      // Remove todos os campos filhos que pertencem a este container de abas
      nextNodes = nextNodes.filter(n => n.parentId !== nodeId);
      // Limpa rastreamento de aba ativa
      delete this.activeTabByTabsId[nodeId];
    }
    
    // Atualiza seleção se o nó removido estava selecionado
    const nextSelected = current.selectedId === nodeId ? null : current.selectedId;
    
    // Emite novo estado com atualização imutável para disparar detecção de mudanças OnPush
    this.stateSubject.next({ nodes: [...nextNodes], selectedId: nextSelected });
  }

  // Remove um item de aba de um container de abas e todos os seus campos filhos
  removeTabItem(tabsNodeId: string, tabId: string): void {
    const current = this.stateSubject.getValue();
    // Remove todos os campos anexados a esta aba
    let nextNodes = current.nodes.filter(n => !(n.parentId === tabsNodeId && n.tabId === tabId));
    // Atualiza itens do container de abas
    nextNodes = nextNodes.map(n => {
      if (n.id !== tabsNodeId || n.type !== 'tabs') return n;
      const props = (n.props as TabsProps) || { items: [] } as TabsProps;
      const items = (props.items || []).filter(i => i.id !== tabId);
      return { ...n, props: { ...(props as any), items } as TabsProps } as BuilderNode;
    });
    // Ajusta aba ativa para primeira restante
    const tabs = nextNodes.find(n => n.id === tabsNodeId);
    if (tabs) {
      const items = ((tabs.props as TabsProps).items || []);
      this.activeTabByTabsId[tabsNodeId] = items[0]?.id ?? null as any;
    }
    const nextSelected = (current.selectedId && current.selectedId === tabId) ? null : current.selectedId;
    this.stateSubject.next({ nodes: nextNodes, selectedId: nextSelected });
  }

  selectNode(id: string | null): void {
    const current = this.stateSubject.getValue();
    const exists = id == null ? true : current.nodes.some(n => n.id === id);
    this.stateSubject.next({ ...current, selectedId: exists ? id : null });
  }

  /**
   *  NOVO: Move um painel para cima na ordem (apenas troca 'order', deixa o grid recalcular posições)
   */
  movePanelUp(panelId: string): void {
    const current = this.stateSubject.getValue();
    const panel = current.nodes.find(n => n.id === panelId);
    
    if (!panel || panel.type !== 'panel') {
      return;
    }
    
    // Encontrar todos os painéis da MESMA TAB (importante para não misturar ordens entre tabs)
    const siblingPanels = current.nodes
      .filter(n => n.type === 'panel' && n.tabId === panel.tabId)
      .map((p, idx) => ({
        node: p,
        currentOrder: (p.props as any)?.order ?? idx
      }))
      .sort((a, b) => a.currentOrder - b.currentOrder);
    
    const currentIndex = siblingPanels.findIndex(p => p.node.id === panelId);
    
    if (currentIndex <= 0) {
      return;
    }
    
    // Painel atual e painel anterior
    const currentPanel = siblingPanels[currentIndex];
    const previousPanel = siblingPanels[currentIndex - 1];
    
    // ✅ Trocar APENAS a propriedade 'order' (deixa row/col serem recalculados automaticamente)
    this.updateNodeProps(currentPanel.node.id, {
      order: currentIndex - 1
    } as any);
    
    this.updateNodeProps(previousPanel.node.id, {
      order: currentIndex
    } as any);
    
    
  }
  
  /**
   *  NOVO: Move um painel para baixo na ordem (apenas troca 'order', deixa o grid recalcular posições)
   */
  movePanelDown(panelId: string): void {
    const current = this.stateSubject.getValue();
    const panel = current.nodes.find(n => n.id === panelId);
    
    if (!panel || panel.type !== 'panel') {
      return;
    }
    
    // Encontrar todos os painéis da MESMA TAB (importante para não misturar ordens entre tabs)
    const siblingPanels = current.nodes
      .filter(n => n.type === 'panel' && n.tabId === panel.tabId)
      .map((p, idx) => ({
        node: p,
        currentOrder: (p.props as any)?.order ?? idx
      }))
      .sort((a, b) => a.currentOrder - b.currentOrder);
    
    const currentIndex = siblingPanels.findIndex(p => p.node.id === panelId);
    
    if (currentIndex === -1 || currentIndex >= siblingPanels.length - 1) {
      return;
    }
    
    // Painel atual e próximo painel
    const currentPanel = siblingPanels[currentIndex];
    const nextPanel = siblingPanels[currentIndex + 1];
    
    // ✅ Trocar APENAS a propriedade 'order' (deixa row/col serem recalculados automaticamente)
    this.updateNodeProps(currentPanel.node.id, {
      order: currentIndex + 1
    } as any);
    
    this.updateNodeProps(nextPanel.node.id, {
      order: currentIndex
    } as any);
    
    
  }

  /**
   *  NOVO: Move qualquer componente para cima (genérico - funciona para todos os tipos)
   */
  moveComponentUp(componentId: string): void {
    const current = this.stateSubject.getValue();
    const component = current.nodes.find(n => n.id === componentId);
    
    if (!component) {
      return;
    }
    
    //  Encontrar TODOS os componentes irmãos (mesma tab, mesmo parentId)
    const siblings = current.nodes
      .filter(n => {
        // Mesma tab e mesmo parentId (ou ambos null para filhos diretos da tab)
        if (n.tabId !== component.tabId || n.parentId !== component.parentId) return false;
        
        // Se for filho direto da tab, separar Panels de outros componentes
        if (!component.parentId) {
          // Panels só ordenam com Panels
          if (component.type === 'panel' && n.type !== 'panel') return false;
          if (component.type !== 'panel' && n.type === 'panel') return false;
        }
        
        return true;
      })
      .map((n, idx) => ({
        node: n,
        currentOrder: (n.props as any)?.order ?? idx
      }))
      .sort((a, b) => a.currentOrder - b.currentOrder);
    
    const currentIndex = siblings.findIndex(s => s.node.id === componentId);
    
    if (currentIndex <= 0) {
      return;
    }
    
    // ✅ CORRIGIDO: Normalizar TODOS os orders dos siblings para valores sequenciais
    // Primeiro, mover o componente para a posição anterior na lista ordenada
    const reordered = [...siblings];
    const [moved] = reordered.splice(currentIndex, 1);
    reordered.splice(currentIndex - 1, 0, moved);
    
    // Atribuir orders sequenciais a TODOS os siblings
    reordered.forEach((s, idx) => {
      this.updateNodeProps(s.node.id, { order: idx } as any);
    });
  }
  
  /**
   *  NOVO: Move qualquer componente para baixo (genérico - funciona para todos os tipos)
   */
  moveComponentDown(componentId: string): void {
    const current = this.stateSubject.getValue();
    const component = current.nodes.find(n => n.id === componentId);
    
    if (!component) {
      return;
    }
    
    //  Encontrar TODOS os componentes irmãos (mesma tab, mesmo parentId)
    const siblings = current.nodes
      .filter(n => {
        // Mesma tab e mesmo parentId (ou ambos null para filhos diretos da tab)
        if (n.tabId !== component.tabId || n.parentId !== component.parentId) return false;
        
        // Se for filho direto da tab, separar Panels de outros componentes
        if (!component.parentId) {
          // Panels só ordenam com Panels
          if (component.type === 'panel' && n.type !== 'panel') return false;
          if (component.type !== 'panel' && n.type === 'panel') return false;
        }
        
        return true;
      })
      .map((n, idx) => ({
        node: n,
        currentOrder: (n.props as any)?.order ?? idx
      }))
      .sort((a, b) => a.currentOrder - b.currentOrder);
    
    const currentIndex = siblings.findIndex(s => s.node.id === componentId);
    
    if (currentIndex === -1 || currentIndex >= siblings.length - 1) {
      return;
    }
    
    // ✅ CORRIGIDO: Normalizar TODOS os orders dos siblings para valores sequenciais
    // Primeiro, mover o componente para a posição seguinte na lista ordenada
    const reordered = [...siblings];
    const [moved] = reordered.splice(currentIndex, 1);
    reordered.splice(currentIndex + 1, 0, moved);
    
    // Atribuir orders sequenciais a TODOS os siblings
    reordered.forEach((s, idx) => {
      this.updateNodeProps(s.node.id, { order: idx } as any);
    });
  }

  /**
   *  LEGACY: Mantido para compatibilidade, agora usa a função genérica
   */
  moveRowUp(rowId: string): void {
    this.moveComponentUp(rowId);
  }
  
  /**
   *  LEGACY: Mantido para compatibilidade, agora usa a função genérica
   */
  moveRowDown(rowId: string): void {
    this.moveComponentDown(rowId);
  }

  /**
   * Move um node para dentro de outro container (panel ou row).
   * Atualiza parentId, herda tabId do destino e recalcula order.
   * Se o node for um row, também atualiza tabId de todos os filhos recursivamente.
   */
  moveNodeToContainer(nodeId: string, targetContainerId: string): void {
    const current = this.stateSubject.getValue();
    const node = current.nodes.find(n => n.id === nodeId);
    const target = current.nodes.find(n => n.id === targetContainerId);
    if (!node || !target) return;

    const siblings = current.nodes.filter(n => n.parentId === targetContainerId);
    const maxOrder = siblings.reduce((max, n) => Math.max(max, (n.props as any)?.order ?? 0), -1);

    const targetTabId = target.tabId ?? null;

    const childIdsOfRow = node.type === 'row'
      ? this.collectDescendantIds(nodeId, current.nodes)
      : new Set<string>();

    const nextNodes = current.nodes.map(n => {
      if (n.id === nodeId) {
        const updatedProps = { ...n.props, order: maxOrder + 1 } as any;
        if (target.type === 'row' && (updatedProps.colSpan ?? 24) > 12) {
          updatedProps.colSpan = 12;
        }
        return { ...n, parentId: targetContainerId, tabId: targetTabId, props: updatedProps };
      }
      if (childIdsOfRow.has(n.id)) {
        return { ...n, tabId: targetTabId };
      }
      return n;
    });

    this.stateSubject.next({ ...current, nodes: nextNodes });
  }

  /**
   * Move múltiplos nodes para dentro de um container.
   * Processa em sequência para calcular orders corretamente.
   */
  moveNodesToContainer(nodeIds: string[], targetContainerId: string): void {
    for (const nodeId of nodeIds) {
      this.moveNodeToContainer(nodeId, targetContainerId);
    }
  }

  private collectDescendantIds(parentId: string, nodes: ReadonlyArray<BuilderNode>): Set<string> {
    const result = new Set<string>();
    const queue = [parentId];
    while (queue.length > 0) {
      const id = queue.shift()!;
      for (const n of nodes) {
        if (n.parentId === id && !result.has(n.id)) {
          result.add(n.id);
          queue.push(n.id);
        }
      }
    }
    return result;
  }

  updateNodeProps<T extends Partial<BuilderProps>>(id: string, partial: T): void {
    // Correção de atualização de estado para mudanças do Inspetor: garante merge imutável profundo em node.props
    const current = this.stateSubject.getValue();
    const nextNodes = current.nodes.map(node => {
      if (node.id !== id) return node;
      const existing = node.props as unknown as Record<string, unknown>;
      const patch = partial as unknown as Record<string, unknown>;
      
      // ✅ NOVO: Clonar arrays profundamente para forçar detecção de mudança
      const clonedPatch: any = {};
      for (const key in patch) {
        if (Array.isArray(patch[key])) {
          // Clonar array profundamente (importante para columns do DataGrid)
          clonedPatch[key] = JSON.parse(JSON.stringify(patch[key]));
        } else {
          clonedPatch[key] = patch[key];
        }
      }
      
      const merged = { ...existing, ...clonedPatch } as BuilderProps;
      
      //  NOVO: Incrementar updateKey para forçar re-render do webhook-adapter
      const currentUpdateKey = node.updateKey ?? 0;
      return { ...node, props: merged, updateKey: currentUpdateKey + 1 } as BuilderNode;
    });
    this.stateSubject.next({ ...current, nodes: nextNodes });
  }

  private createDefaultProps(type: BuilderComponentType): BuilderProps {
    if (type === 'text') {
      const props: TextProps = { label: '', placeholder: '', colSpan: 24, rowSpan: 1 };
      return props;
    }
    if (type === 'textarea') {
      const props: TextAreaProps = { label: '', placeholder: '', colSpan: 24, rowSpan: 2, minRows: 4 };
      return props;
    }
    if (type === 'number') {
      const props: NumberProps = { label: '', placeholder: '', colSpan: 24, rowSpan: 1, min: 0, max: 100 };
      return props;
    }
    if (type === 'switch') {
      const props: SwitchProps = { label: '', colSpan: 24, rowSpan: 1, defaultValue: false };
      return props;
    }
    if (type === 'simpletable') {
      const tableConfiguration: TableConfiguration = {
        showAdd: false,
        showRemove: false,
        fields: [
          {
            field: 'name',
            label: '',
            placeholder: '',
            type: 'text',
            colspan: 1,
            noLabel: true,
            required: true
          },
          {
            field: 'value',
            label: '',
            placeholder: '',
            type: 'text',
            colspan: 1,
            noLabel: true,
            required: true
          }
        ]
      };
      const props: SimpleTableProps = { 
        label: '', 
        colSpan: 24, 
        rowSpan: 3, 
        dataSource: [], 
        columns: [],
        tableConfiguration
      };
      return props;
    }
    if (type === 'table') {
      const props: TableProps = { label: '', colSpan: 24, rowSpan: 4, dataSource: [], columns: [], sorting: true, filtering: true, pagination: true };
      return props;
    }
    // V5 Webhook component defaults
    if (type === 'date') {
      const props: DateProps = { label: '', placeholder: '', colSpan: 24, rowSpan: 1, dateFormat: 'DD/MM/YYYY', showTime: false, required: false };
      return props;
    }
    if (type === 'select') {
      const props: SelectProps = { label: '', placeholder: '', colSpan: 24, rowSpan: 1, options: [], multiple: false, showSearch: false, required: false };
      return props;
    }
    if (type === 'dropdown') {
      const props: DropdownProps = { label: '', placeholder: 'Selecione uma opção', colSpan: 24, rowSpan: 1, options: [], multiple: false, showSearch: false, required: false };
      return props;
    }
    if (type === 'querySelect') {
      const props: QuerySelectProps = { label: '', placeholder: '', colSpan: 24, rowSpan: 1, condition: '', showKey: false, entityName: '', required: false };
      return props;
    }
    if (type === 'keyValue') {
      const props: KeyValueProps = { label: '', colSpan: 24, rowSpan: 2, autocompleteKey: [], autocompleteValue: [], required: false };
      return props;
    }
    if (type === 'keyValueObject') {
      const props: KeyValueObjectProps = { label: '', colSpan: 24, rowSpan: 3, required: false };
      return props;
    }
    if (type === 'tag') {
      const props: TagProps = { label: '', placeholder: '', colSpan: 24, rowSpan: 1, options: [], required: false };
      return props;
    }
    if (type === 'search') {
      const props: SearchProps = { 
        label: '', 
        placeholder: '', 
        colSpan: 24, 
        rowSpan: 1, 
        showKey: false, 
        required: false,
        searchProperties: {
          entityName: 'Mktpproducts', //  Valor padrão útil ao invés de vazio
          fields: {
            keyField: 'CODEPRODUCT',  //  Valor padrão útil
            valueField: 'NAME'         //  Valor padrão útil
          }
        }
      };
      return props;
    }
    if (type === 'divider') {
      const props: DividerProps = { label: '', colSpan: 24, rowSpan: 1, dashed: false, type: 'horizontal', orientation: 'center', color: '#d9d9d9' };
      return props;
    }
    if (type === 'row') {
      const props: RowProps = { 
        label: 'Linha', 
        colSpan: 24, 
        rowSpan: 1, 
        gap: '12px',
        alignItems: 'start',
        justifyContent: 'start',
        order: 0
      };
      return props;
    }
    if (type === 'section') {
      const props: SectionProps = { label: '', colSpan: 24, rowSpan: 1, title: '', collapsible: false, collapsed: false };
      return props;
    }
    if (type === 'button') {
      const props: ButtonProps = { 
        label: '', 
        text: 'Button',
        buttonType: 'default',
        size: 'default',
        colSpan: 24, 
        rowSpan: 1,
        disabled: false,
        loading: false
      };
      return props;
    }
    if (type === 'image') {
      const props: ImageProps = {
        label: 'builderScreen.palette.image',
        imageUrl: 'https://via.placeholder.com/300x200',
        alt: 'Imagem de exemplo',
        width: 300,
        height: 200,
        borderRadius: 0,
        enableLightbox: true,
        dataPath: '',
        colSpan: 24,
        rowSpan: 3
      };
      return props;
    }
    if (type === 'panel') {
      const props: PanelProps = { 
        name: 'Panel',
        active: false,
        disabled: false,
        order: 1,
        collapsible: true,
        collapsed: false,
        colSpan: 24,
        rowSpan: 1,
      };
      return props;
    }
    if (type === 'datagrid') {
      const props: DataGridProps = {
        label: 'Grade de Dados',
        colSpan: 24,
        rowSpan: 6,
        columns: [
          {
            field: 'id',
            header: 'ID',
            type: 'number',
            dataPath: 'id',
            width: '80px',
            sortable: true,
            filterable: false,
            editable: false
          },
          {
            field: 'name',
            header: 'Nome',
            type: 'text',
            dataPath: 'name',
            sortable: true,
            filterable: true,
            editable: false
          }
        ],
        dataPath: 'data',
        enablePagination: true,
        pageSize: 10,
        maxVisibleRows: 0,
        enableFilters: false,
        enableSorting: true,
        enableCRUD: false,
        selectionMode: 'none'
      };
      return props;
    }
    // Caso padrão para 'tabs'
    const props: TabsProps = {
      label: 'builderScreen.tabs.label',
      items: [
        { id: 'general', name: 'builderScreen.tab.general', icon: 'setting', themeIcon: 'twotone', twotoneColor: '#52c41a', disabled: false },
      ],
      colSpan: 24,
      rowSpan: 1,
    };
    return props;
  }

  private generateId(prefix: string): string {
    return `${prefix}-${Math.random().toString(36).slice(2, 8)}-${Date.now().toString(36)}`;
  }

  private mapToJson(nodes: ReadonlyArray<BuilderNode>): BuilderJsonOutput | WebhookConfiguration {
    // SMART DETECTION: Detectar se há panels para usar estrutura webhook
    const hasPanel = nodes.some(n => n.type === 'panel');
    
    if (hasPanel) {
      // Se tem panels, gerar JSON webhook
      return this.generateWebhookJson(nodes);
    } else {
      // Se não tem panels, gerar JSON padrão (mantém compatibilidade)
      return this.generateStandardJsonLegacy(nodes);
    }
  }

  /**
   * Gera JSON webhook quando panels estão presentes
   */
  private generateWebhookJson(nodes: ReadonlyArray<BuilderNode>): WebhookConfiguration {
    const webhookConfig: any = {
      tabs: [],
      panels: [],
      fields: [],
      rows: []
    };

    // Processar tabs
    const tabsNode = nodes.find(n => n.type === 'tabs');
    if (tabsNode) {
      const tabItems = (tabsNode.props as TabsProps).items || [];
      const tabProps = tabsNode.props as TabsProps;
      webhookConfig.tabs = tabItems.map(tab => ({
        id: tab.id,
        name: tab.name,
        icon: tab.icon || 'appstore',
        themeIcon: tab.themeIcon || 'outline',
        twotoneColor: tab.twotoneColor || '#1890ff',
        disabled: !!tab.disabled,
        backgroundColor: tabProps.backgroundColor,
        textColor: tabProps.textColor,
        activeTabColor: tabProps.activeTabColor,
        activeTabTextColor: tabProps.activeTabTextColor,
        borderColor: tabProps.borderColor,
        contentBackgroundColor: tabProps.contentBackgroundColor,
        contentTextColor: tabProps.contentTextColor,
        hoverTabColor: tabProps.hoverTabColor,
        tabsContainerBackgroundColor: tabProps.tabsContainerBackgroundColor,
        tabsContainerBorderColor: tabProps.tabsContainerBorderColor,
        tabsContainerBorderRadius: tabProps.tabsContainerBorderRadius,
        tabsContainerPadding: tabProps.tabsContainerPadding,
        wrapperBackgroundColor: tabProps.wrapperBackgroundColor,
        wrapperBorderColor: tabProps.wrapperBorderColor,
        wrapperBorderRadius: tabProps.wrapperBorderRadius,
        wrapperPadding: tabProps.wrapperPadding
      }));

      // Barra de Contexto: salvar contextFields no JSON
      const contextFields = (tabProps as any).contextFields;
      if (contextFields && contextFields.length > 0) {
        (webhookConfig as any).contextFields = contextFields;
      }
    }

    // Processar panels
    const panelNodes = nodes.filter(n => n.type === 'panel');
    webhookConfig.panels = panelNodes.map((panel, index) => ({
      id: panel.id,
      name: (panel.props as PanelProps).name || `Panel ${index + 1}`,
      active: (panel.props as PanelProps).active ?? (index === 0),
      disabled: (panel.props as PanelProps).disabled ?? false,
      tab: panel.tabId || 'main',
      order: (panel.props as PanelProps).order ?? index,
      collapsible: (panel.props as PanelProps).collapsible ?? false,
      collapsed: (panel.props as PanelProps).collapsed ?? false,
      backgroundColor: (panel.props as PanelProps).backgroundColor,
      textColor: (panel.props as PanelProps).textColor,
      panelBorderColor: (panel.props as PanelProps).panelBorderColor,
      panelHeaderBackgroundColor: (panel.props as PanelProps).panelHeaderBackgroundColor,
      panelHeaderTextColor: (panel.props as PanelProps).panelHeaderTextColor,
      panelContentBackgroundColor: (panel.props as PanelProps).panelContentBackgroundColor,
      colSpan: (panel.props as any).colSpan ?? 24,
      rowSpan: (panel.props as any).rowSpan ?? 1,
      col: (panel.props as any).col ?? 0,
      row: (panel.props as any).row ?? 0
    }));

    // Separar Rows de fields normais
    const rowNodes = nodes.filter(n => n.type === 'row');
    const fieldNodes = nodes.filter(n => 
      n.type !== 'tabs' && n.type !== 'panel' && n.type !== 'row'
    );
    
    // Processar Rows
    webhookConfig.rows = rowNodes.map((row, index) => {
      const rowField = this.convertNodeToWebhookField(row, row.parentId || 'main', index);
      return rowField as any;
    }).filter(row => row !== null) as any[];
    
    // Processar fields normais
    webhookConfig.fields = fieldNodes.map((field, index) => {
      let correctPanelId = field.parentId || 'main';
      
      const parentNode = nodes.find(n => n.id === field.parentId);
      const isParentContainer = parentNode && (parentNode.type === 'row');
      
      if (field.parentId && !panelNodes.some(p => p.id === field.parentId) && !isParentContainer) {
        const fieldTab = field.tabId;
        const matchingPanel = panelNodes.find(p => p.tabId === fieldTab);
        if (matchingPanel) {
          correctPanelId = matchingPanel.id;
        }
      }
      
      const webhookField = this.convertNodeToWebhookField(field, correctPanelId, index);
      webhookField.panel = correctPanelId;
      
      return webhookField;
    }).filter(field => field !== null) as WebhookField[];

    return webhookConfig;
  }

  /**
   * Gera JSON padrão quando não há panels (backward compatibility)
   */
  private generateStandardJsonLegacy(nodes: ReadonlyArray<BuilderNode>): BuilderJsonOutput {
    const webhookConfig: WebhookConfiguration = {
      tabs: [],
      panels: [],
      fields: [],
    };

    // Abas: suporta o primeiro container de abas para v1; agrega seus itens
    const tabsNode = nodes.find(n => n.type === 'tabs');
    const tabItems = tabsNode ? ((tabsNode.props as TabsProps).items || []) : [];
    
    // Separar nodes que não são tabs para verificar se precisamos de estrutura padrão
    const nonTabs = nodes.filter(n => n.type !== 'tabs');
    
    // Gerar tabs conforme estrutura webhook
    const tabs: WebhookTab[] = tabItems.map(t => ({
      id: t.id,
      name: t.name,
      icon: t.icon,
      themeIcon: t.themeIcon,
      twotoneColor: t.twotoneColor,
      disabled: !!t.disabled,
    }));

    // Se não há tabs definidas, criar uma tab padrão
    if (tabs.length === 0 && nonTabs.length > 0) {
      tabs.push({
        id: 'main',
        name: 'Principal',
        icon: 'appstore',
        themeIcon: 'outline',
        twotoneColor: '#1890ff',
        disabled: false,
      });
    }

    webhookConfig.tabs = tabs;

    // Gerar panels para cada tab (estrutura webhook: 1 panel por tab como padrão)
    let panelOrder = 0;
    const panels: WebhookPanel[] = tabs.map((tab, idx) => ({
      id: `panel_${tab.id}`,
      name: tab.name,
      active: idx === 0, // Primeiro panel ativo
      disabled: tab.disabled,
      tab: tab.id, // Referência à tab pai
      order: panelOrder++,
    }));

    webhookConfig.panels = panels;

    // Converter nodes para fields webhook
    const fields: WebhookField[] = [];
    nonTabs.forEach((n, idx) => {
      const panelId = n.tabId ? `panel_${n.tabId}` : panels[0]?.id || 'panel_main';
      const webhookField = this.convertNodeToWebhookField(n, panelId, idx);
      
      if (webhookField) {
        // Preservar atributos customizados (colors, etc.)
        if (n.props && this.extractColorProperties(n.props)) {
          (webhookField as any).colors = this.extractColorProperties(n.props);
        }
        fields.push(webhookField);
      }
    });

    webhookConfig.fields = fields;

    // Para compatibilidade, retornar como BuilderJsonOutput
    return webhookConfig as any;
  }

  /**
   * Converte BuilderNode para WebhookField mantendo todas as propriedades
   */
  private convertNodeToWebhookField(node: BuilderNode, panelId: string, rowIndex: number): WebhookField | null {
    // ✅ PRESERVA O FIELD NAME ORIGINAL se existir nas props
    const originalFieldName = (node.props as any)?.fieldName || (node.props as any)?.field;
    //  IMPORTANTE: Para containers (Row), usar dataPath ou node.id como field (estável, curto)
    const fieldName = (node.type === 'row')
      ? ((node.props as any)?.dataPath || node.id)
      : (originalFieldName || `field_${node.type}-${this.generateId('field')}`);
    
    const baseField: WebhookField = {
      field: fieldName, // ✅ Usa field name preservado (ou node.id para containers)
      type: this.mapBuilderTypeToWebhookType(node.type),
      label: (node.props as any)?.label || '',
        panel: panelId,
        col: (node.props as any)?.col ?? 0, // ✅ Preservar col do node
      row: (node.props as any)?.row ?? rowIndex, // ✅ Preservar row do node, usar rowIndex como fallback
      colSpan: (node.props as any)?.colSpan ?? 24,
      rowSpan: (node.props as any)?.rowSpan ?? 1, // ✅ Preservar rowSpan do node
      order: (node.props as any)?.order ?? rowIndex, // ✅ NOVO: Preservar order para ordenação dentro do panel
      required: (node.props as any)?.required ?? false,
      placeholder: (node.props as any)?.placeholder,
      tooltip: (node.props as any)?.tooltip,
      isDisabled: (node.props as any)?.isDisabled ?? (node.props as any)?.disabled ?? false,
      hidden: (node.props as any)?.hidden ?? false,
    } as any; //  Cast para 'any' para permitir adicionar dataPath
    
    //  NOVO: Adicionar dataPath se existir (importante para Master-Detail)
    if ((node.props as any)?.dataPath) {
      (baseField as any).dataPath = (node.props as any).dataPath;
    }

    //  NOVO: Adicionar events se existirem (Event Scripting System)
    if ((node.props as any)?.events) {
      (baseField as any).events = (node.props as any).events;
    }

    // Adicionar propriedades específicas por tipo
    switch (node.type) {
      case 'text':
        const textField: any = {
          ...baseField,
          minLength: (node.props as any)?.minLength,
          maxLength: (node.props as any)?.maxLength,
          backgroundColor: (node.props as any)?.backgroundColor,
          textColor: (node.props as any)?.textColor,
          defaultValue: (node.props as any)?.defaultValue,
        };
        if ((node.props as any)?.fieldMappings?.length > 0) textField.fieldMappings = (node.props as any).fieldMappings;
        if ((node.props as any)?.dependencies?.length > 0) textField.dependencies = (node.props as any).dependencies;
        return textField as WebhookField;

      case 'textarea':
        const textareaField: any = {
          ...baseField,
          minRows: (node.props as any)?.minRows,
          maxRows: (node.props as any)?.maxRows,
          backgroundColor: (node.props as any)?.backgroundColor,
          textColor: (node.props as any)?.textColor,
          defaultValue: (node.props as any)?.defaultValue,
        };
        if ((node.props as any)?.fieldMappings?.length > 0) textareaField.fieldMappings = (node.props as any).fieldMappings;
        if ((node.props as any)?.dependencies?.length > 0) textareaField.dependencies = (node.props as any).dependencies;
        return textareaField as WebhookField;

      case 'number':
        const numberField: any = {
          ...baseField,
          min: (node.props as any)?.min,
          max: (node.props as any)?.max,
          backgroundColor: (node.props as any)?.backgroundColor,
          textColor: (node.props as any)?.textColor,
          defaultValue: (node.props as any)?.defaultValue,
          decimalPlaces: (node.props as any)?.decimalPlaces ?? 2,
          formatThousands: (node.props as any)?.formatThousands ?? false,
          thousandSeparator: (node.props as any)?.thousandSeparator ?? '.',
          decimalSeparator: (node.props as any)?.decimalSeparator ?? ',',
          addonBefore: (node.props as any)?.addonBefore ?? '',
          addonAfter: (node.props as any)?.addonAfter ?? '',
        };
        if ((node.props as any)?.fieldMappings?.length > 0) numberField.fieldMappings = (node.props as any).fieldMappings;
        if ((node.props as any)?.dependencies?.length > 0) numberField.dependencies = (node.props as any).dependencies;
        return numberField as WebhookField;

      case 'select':
        return {
          ...baseField,
          options: (node.props as any)?.options || [],
          backgroundColor: (node.props as any)?.backgroundColor,
          textColor: (node.props as any)?.textColor,
        } as WebhookField;

      case 'dropdown':
        const dropdownField: any = {
          ...baseField,
          type: 'dropdown',
          options: (node.props as any)?.options || [],
          multiple: (node.props as any)?.multiple || false,
          showSearch: (node.props as any)?.showSearch || false,
          backgroundColor: (node.props as any)?.backgroundColor,
          textColor: (node.props as any)?.textColor,
          defaultValue: (node.props as any)?.defaultValue,
        };
        if ((node.props as any)?.fieldMappings?.length > 0) dropdownField.fieldMappings = (node.props as any).fieldMappings;
        if ((node.props as any)?.dependencies?.length > 0) dropdownField.dependencies = (node.props as any).dependencies;
        return dropdownField as WebhookField;

      case 'querySelect':
        return {
          ...baseField,
          condition: (node.props as any)?.condition,
          showKey: (node.props as any)?.showKey ?? false,
          backgroundColor: (node.props as any)?.backgroundColor,
          textColor: (node.props as any)?.textColor,
        } as WebhookField;

      case 'date':
        const dateField: any = {
          ...baseField,
          dateFormat: (node.props as any)?.dateFormat,
          backgroundColor: (node.props as any)?.backgroundColor,
          textColor: (node.props as any)?.textColor,
          defaultValue: (node.props as any)?.defaultValue,
        };
        if ((node.props as any)?.fieldMappings?.length > 0) dateField.fieldMappings = (node.props as any).fieldMappings;
        if ((node.props as any)?.dependencies?.length > 0) dateField.dependencies = (node.props as any).dependencies;
        return dateField as WebhookField;

      case 'switch':
        const switchField: any = {
          ...baseField,
          defaultValue: (node.props as any)?.defaultValue,
          checkedChildren: (node.props as any)?.checkedChildren,
          unCheckedChildren: (node.props as any)?.unCheckedChildren,
          backgroundColor: (node.props as any)?.backgroundColor,
          textColor: (node.props as any)?.textColor,
        };
        const tabControl = (node.props as any)?.tabControl;
        if (tabControl) {
          switchField.tabControl = tabControl;
        }
        if ((node.props as any)?.fieldMappings?.length > 0) switchField.fieldMappings = (node.props as any).fieldMappings;
        if ((node.props as any)?.dependencies?.length > 0) switchField.dependencies = (node.props as any).dependencies;
        return switchField as WebhookField;

      case 'button':
        const buttonProps = node.props as any;
        const buttonField: any = {
          ...baseField,
          label: buttonProps?.text || buttonProps?.label || '',
          text: buttonProps?.text,
          buttonType: buttonProps?.buttonType,
          size: buttonProps?.size,
          disabled: buttonProps?.disabled,
          loading: buttonProps?.loading,
          icon: buttonProps?.icon,
          backgroundColor: buttonProps?.backgroundColor,
          textColor: buttonProps?.textColor,
          actionType: buttonProps?.actionType,
          flowConfig: buttonProps?.flowConfig,
          flowDataMode: buttonProps?.flowDataMode ?? 'full',
        };
        return buttonField;

      case 'keyValue':
        return {
          ...baseField,
          autocompleteKey: (node.props as any)?.autocompleteKey,
          autocompleteValue: (node.props as any)?.autocompleteValue,
          backgroundColor: (node.props as any)?.backgroundColor,
          textColor: (node.props as any)?.textColor,
        } as WebhookField;

      case 'keyValueObject':
        return {
          ...baseField,
          autocompleteKey: (node.props as any)?.autocompleteKey,
          autocompleteValue: (node.props as any)?.autocompleteValue,
          backgroundColor: (node.props as any)?.backgroundColor,
          textColor: (node.props as any)?.textColor,
        } as WebhookField;

      case 'tag':
        return {
          ...baseField,
          options: (node.props as any)?.options || [],
          backgroundColor: (node.props as any)?.backgroundColor,
          textColor: (node.props as any)?.textColor,
        } as WebhookField;

      case 'search':
        const searchField: any = {
          ...baseField,
          showKey: (node.props as any)?.showKey ?? false,
          searchProperties: (node.props as any)?.searchProperties || {
            entityName: '',
            fields: {
              keyField: '',
              valueField: '',
            }
          },
          backgroundColor: (node.props as any)?.backgroundColor,
          textColor: (node.props as any)?.textColor,
        };
        // Serializar dependências de pesquisa (cascading search)
        if ((node.props as any)?.dependencies?.length > 0) {
          searchField.dependencies = (node.props as any).dependencies;
        }
        // Serializar mapeamentos de campos derivados (field mappings)
        if ((node.props as any)?.fieldMappings?.length > 0) {
          searchField.fieldMappings = (node.props as any).fieldMappings;
        }
        return searchField as WebhookField;

      case 'divider':
        return {
          ...baseField,
          divider: {
            dashed: (node.props as any)?.dashed ?? false,
            type: (node.props as any)?.type ?? 'horizontal',
            orientation: (node.props as any)?.orientation ?? 'center',
            variant: 'solid',
            color: (node.props as any)?.color ?? '#d9d9d9',
          },
        };

      case 'section':
        return {
          ...baseField,
          sectionConfiguration: {
            fields: [], // Será preenchido se necessário
          },
        };

      case 'datagrid':
        const datagridProps = node.props as any;
        return {
          ...baseField,
          type: 'datagrid',
          label: datagridProps?.label || '', //  NOVO: Label customizável da grade
          dataPath: datagridProps?.dataPath,
          columns: datagridProps?.columns || [],
          enablePagination: datagridProps?.enablePagination ?? true,
          pageSize: datagridProps?.pageSize ?? 10,
          maxVisibleRows: datagridProps?.maxVisibleRows ?? 0,
          enableFilters: datagridProps?.enableFilters ?? false,
          enableSorting: datagridProps?.enableSorting ?? true,
          enableCRUD: datagridProps?.enableCRUD ?? false,
          selectionMode: datagridProps?.selectionMode ?? 'none',
          //  NOVO: Incluir propriedades Master-Detail na exportação
          enableMasterDetail: datagridProps?.enableMasterDetail ?? false,
          masterDetailFormPanel: datagridProps?.masterDetailFormPanel || null,
          masterDetailFormPanels: datagridProps?.masterDetailFormPanels || [],
          masterDetailAutoGenerate: datagridProps?.masterDetailAutoGenerate ?? false,
          autoAddFirstRow: datagridProps?.autoAddFirstRow ?? false,
          autoAddOrder: datagridProps?.autoAddOrder ?? 0,
          viewMode: datagridProps?.viewMode ?? 'inline',
          drawerWidth: datagridProps?.drawerWidth ?? 40
        } as any;

      case 'row':
        //  NOVO: Serializar Row como um field especial
        const rowProps = node.props as any;
        return {
          ...baseField,
          type: 'row',
          label: rowProps?.label || 'Linha',
          gap: rowProps?.gap || '12px',
          alignItems: rowProps?.alignItems || 'start',
          justifyContent: rowProps?.justifyContent || 'start',
          backgroundColor: rowProps?.backgroundColor,
          padding: rowProps?.padding || '8px',
          order: rowProps?.order ?? 0,
        } as any;

      case 'image':
        //  NOVO: Serializar Image com todas as propriedades
        const imageProps = node.props as any;
        return {
          ...baseField,
          type: 'image',
          imageUrl: imageProps?.imageUrl || 'https://via.placeholder.com/300x200',
          alt: imageProps?.alt || 'Imagem',
          width: imageProps?.width || 300,
          height: imageProps?.height || 200,
          borderRadius: imageProps?.borderRadius || 0,
          enableLightbox: imageProps?.enableLightbox ?? true,
          dataPath: imageProps?.dataPath || ''
        } as any;

      // Adicionar mais tipos conforme necessário
      default:
        return baseField;
    }
  }

  /**
   * Mapeia tipos de componente builder para webhook
   */
  private mapBuilderTypeToWebhookType(builderType: string): string {
    const typeMapping: { [key: string]: string } = {
      text: 'text',
      textarea: 'textarea',
      number: 'number',
      select: 'select',
      querySelect: 'querySelect',
      date: 'date',
      switch: 'switch',
      button: 'button',
      divider: 'divider',
      section: 'section',
      keyValue: 'keyValue',
      keyValueObject: 'keyValueObject',
      tag: 'tag',
      search: 'search',
      simpletable: 'table',
      table: 'table',
      datagrid: 'datagrid',
      row: 'row', //  NOVO: Mapear tipo Row
      image: 'image', //  NOVO: Mapear tipo Image
    };

    return typeMapping[builderType] || 'text';
  }

  /**
   * Converte WebhookField para BuilderNode
   */
  private convertWebhookFieldToNode(field: WebhookField, nodeCounter: number): BuilderNode | null {
    const nodeId = `field-${nodeCounter}`;
    
    // ✅ CORREÇÃO: Detectar dropdowns antigos que foram salvos como "select"
    // Se o tipo é "select" mas o nome do field contém "dropdown", tratar como dropdown
    let effectiveType = field.type;
    if (field.type === 'select' && field.field && field.field.includes('dropdown')) {
      effectiveType = 'dropdown';      
    }
    
    const baseProps: any = {
      label: field.label,
      placeholder: field.placeholder,
      required: field.required || false,
      isDisabled: field.isDisabled || false,
      colSpan: field.colSpan || 24,
      rowSpan: field.rowSpan || 1, // ✅ Preservar rowSpan
      row: field.row || 0, // Preservar propriedade row
      col: field.col || 0, // Preservar propriedade col
      order: (field as any).order ?? 0, // ✅ NOVO: Preservar order para ordenação dentro do panel
      tooltip: field.tooltip,
      // 🔑 CRÍTICO: SEMPRE preservar field name original
      fieldName: field.field, // Armazena o field name original para exportação futura
      field: field.field, // Mantém ambos para compatibilidade
      //  NOVO: Preservar dataPath para Master-Detail
      dataPath: (field as any).dataPath || field.field,
      //  NOVO: Preservar events se existirem (Event Scripting System)
      events: (field as any).events || undefined,
      // Preservar atributos customizados
      ...(field as any).colors && { colors: (field as any).colors }
    };

    // Mapear propriedades específicas por tipo (usando effectiveType para compatibilidade)
    switch (effectiveType) {
      case 'text':
        const textNodeProps: any = {
          ...baseProps,
          minLength: field.minLength,
          maxLength: field.maxLength,
          backgroundColor: (field as any).backgroundColor,
          textColor: (field as any).textColor,
          defaultValue: (field as any).defaultValue,
        };
        if ((field as any).fieldMappings?.length > 0) textNodeProps.fieldMappings = (field as any).fieldMappings;
        if ((field as any).dependencies?.length > 0) textNodeProps.dependencies = (field as any).dependencies;
        return {
          id: nodeId, type: 'text', props: textNodeProps,
          children: undefined, parentId: null, tabId: null
        };

      case 'textarea':
        const textareaNodeProps: any = {
          ...baseProps,
          minRows: field.minRows,
          maxRows: field.maxRows,
          backgroundColor: (field as any).backgroundColor,
          textColor: (field as any).textColor,
          defaultValue: (field as any).defaultValue,
        };
        if ((field as any).fieldMappings?.length > 0) textareaNodeProps.fieldMappings = (field as any).fieldMappings;
        if ((field as any).dependencies?.length > 0) textareaNodeProps.dependencies = (field as any).dependencies;
        return {
          id: nodeId, type: 'textarea', props: textareaNodeProps,
          children: undefined, parentId: null, tabId: null
        };

      case 'number':
        const numberNodeProps: any = {
          ...baseProps,
          min: field.min,
          max: field.max,
          backgroundColor: (field as any).backgroundColor,
          textColor: (field as any).textColor,
          defaultValue: (field as any).defaultValue,
          decimalPlaces: (field as any).decimalPlaces,
          formatThousands: (field as any).formatThousands,
          thousandSeparator: (field as any).thousandSeparator,
          decimalSeparator: (field as any).decimalSeparator,
          addonBefore: (field as any).addonBefore,
          addonAfter: (field as any).addonAfter,
        };
        if ((field as any).fieldMappings?.length > 0) numberNodeProps.fieldMappings = (field as any).fieldMappings;
        if ((field as any).dependencies?.length > 0) numberNodeProps.dependencies = (field as any).dependencies;
        return {
          id: nodeId, type: 'number', props: numberNodeProps,
          children: undefined, parentId: null, tabId: null
        };

      case 'select':
        return {
          id: nodeId,
          type: 'select',
          props: {
            ...baseProps,
            options: field.options || [],
            backgroundColor: (field as any).backgroundColor,
            textColor: (field as any).textColor,
          },
          children: undefined,
          parentId: null,
          tabId: null
        };

      case 'dropdown':
        const dropdownNodeProps: any = {
          ...baseProps,
          options: field.options || [],
          multiple: (field as any).multiple || false,
          showSearch: (field as any).showSearch || false,
          backgroundColor: (field as any).backgroundColor,
          textColor: (field as any).textColor,
          defaultValue: (field as any).defaultValue,
        };
        if ((field as any).fieldMappings?.length > 0) dropdownNodeProps.fieldMappings = (field as any).fieldMappings;
        if ((field as any).dependencies?.length > 0) dropdownNodeProps.dependencies = (field as any).dependencies;
        return {
          id: nodeId, type: 'dropdown', props: dropdownNodeProps,
          children: undefined, parentId: null, tabId: null
        };

      case 'querySelect':
        return {
          id: nodeId,
          type: 'querySelect',
          props: {
            ...baseProps,
            condition: field.condition,
            showKey: field.showKey,
            backgroundColor: (field as any).backgroundColor,
            textColor: (field as any).textColor,
          },
          children: undefined,
          parentId: null,
          tabId: null
        };

      case 'date':
        const dateNodeProps: any = {
          ...baseProps,
          dateFormat: field.dateFormat,
          backgroundColor: (field as any).backgroundColor,
          textColor: (field as any).textColor,
          defaultValue: (field as any).defaultValue,
        };
        if ((field as any).fieldMappings?.length > 0) dateNodeProps.fieldMappings = (field as any).fieldMappings;
        if ((field as any).dependencies?.length > 0) dateNodeProps.dependencies = (field as any).dependencies;
        return {
          id: nodeId, type: 'date', props: dateNodeProps,
          children: undefined, parentId: null, tabId: null
        };

      case 'switch':
        const switchNodeProps: any = {
          ...baseProps,
          defaultValue: field.defaultValue,
          checkedChildren: (field as any).checkedChildren,
          unCheckedChildren: (field as any).unCheckedChildren,
          backgroundColor: (field as any).backgroundColor,
          textColor: (field as any).textColor,
        };
        if ((field as any).tabControl) {
          switchNodeProps.tabControl = (field as any).tabControl;
        }
        if ((field as any).fieldMappings?.length > 0) switchNodeProps.fieldMappings = (field as any).fieldMappings;
        if ((field as any).dependencies?.length > 0) switchNodeProps.dependencies = (field as any).dependencies;
        return {
          id: nodeId, type: 'switch', props: switchNodeProps,
          children: undefined, parentId: null, tabId: null
        };

      case 'button':
        const fieldProps = field as any;
        return {
          id: nodeId,
          type: 'button',
          props: {
            ...baseProps,
            text: fieldProps.text || field.label,
            buttonType: fieldProps.buttonType || 'default',
            size: fieldProps.size || 'default',
            disabled: fieldProps.disabled || false,
            loading: fieldProps.loading || false,
            icon: fieldProps.icon || '',
            backgroundColor: fieldProps.backgroundColor || '',
            textColor: fieldProps.textColor || '',
            actionType: fieldProps.actionType || 'custom',
            flowConfig: fieldProps.flowConfig || null,
            flowDataMode: fieldProps.flowDataMode ?? 'full',
          },
          children: undefined,
          parentId: null,
          tabId: null
        };

      case 'keyValue':
        return {
          id: nodeId,
          type: 'keyValue',
          props: {
            ...baseProps,
            autocompleteKey: field.autocompleteKey,
            autocompleteValue: field.autocompleteValue,
            backgroundColor: (field as any).backgroundColor,
            textColor: (field as any).textColor,
          },
          children: undefined,
          parentId: null,
          tabId: null
        };

      case 'keyValueObject':
        return {
          id: nodeId,
          type: 'keyValueObject',
          props: {
            ...baseProps,
            autocompleteKey: field.autocompleteKey,
            autocompleteValue: field.autocompleteValue,
            backgroundColor: (field as any).backgroundColor,
            textColor: (field as any).textColor,
          },
          children: undefined,
          parentId: null,
          tabId: null
        };

      case 'tag':
        return {
          id: nodeId,
          type: 'tag',
          props: {
            ...baseProps,
            options: field.options || [],
            backgroundColor: (field as any).backgroundColor,
            textColor: (field as any).textColor,
          },
          children: undefined,
          parentId: null,
          tabId: null
        };

      case 'search':
        const searchNodeProps: any = {
          ...baseProps,
          showKey: field.showKey,
          searchProperties: field.searchProperties || {
            entityName: '',
            fields: {
              keyField: '',
              valueField: ''
            }
          },
          backgroundColor: (field as any).backgroundColor,
          textColor: (field as any).textColor,
        };
        // Deserializar dependências de pesquisa (cascading search)
        if ((field as any).dependencies?.length > 0) {
          searchNodeProps.dependencies = (field as any).dependencies;
        }
        // Deserializar mapeamentos de campos derivados (field mappings)
        if ((field as any).fieldMappings?.length > 0) {
          searchNodeProps.fieldMappings = (field as any).fieldMappings;
        }
        return {
          id: nodeId,
          type: 'search',
          props: searchNodeProps,
          children: undefined,
          parentId: null,
          tabId: null
        };

      case 'divider':
        return {
          id: nodeId,
          type: 'divider',
          props: {
            ...baseProps,
            dashed: field.divider?.dashed,
            type: field.divider?.type,
            orientation: field.divider?.orientation,
            color: field.divider?.color,
          },
          children: undefined,
          parentId: null,
          tabId: null
        };

      case 'section':
        return {
          id: nodeId,
          type: 'section',
          props: {
            ...baseProps,
            title: field.label,
            collapsible: true,
            collapsed: false,
          },
          children: undefined,
          parentId: null,
          tabId: null
        };

      case 'datagrid':
        const datagridField = field as any;
        return {
          id: nodeId,
          type: 'datagrid',
          props: {
            ...baseProps,
            label: datagridField.label || datagridField.label === '' ? datagridField.label : field.label, //  NOVO: Preservar label customizável
            dataPath: datagridField.dataPath,
            columns: datagridField.columns || [],
            enablePagination: datagridField.enablePagination ?? true,
            pageSize: datagridField.pageSize ?? 10,
            maxVisibleRows: datagridField.maxVisibleRows ?? 0,
            enableFilters: datagridField.enableFilters ?? false,
            enableSorting: datagridField.enableSorting ?? true,
            enableCRUD: datagridField.enableCRUD ?? false,
            selectionMode: datagridField.selectionMode ?? 'none',
            //  NOVO: Preservar propriedades Master-Detail
            enableMasterDetail: datagridField.enableMasterDetail ?? false,
            masterDetailFormPanel: datagridField.masterDetailFormPanel || null,
            masterDetailFormPanels: datagridField.masterDetailFormPanels || [],
            masterDetailAutoGenerate: datagridField.masterDetailAutoGenerate ?? false,
            autoAddFirstRow: datagridField.autoAddFirstRow ?? false,
            autoAddOrder: datagridField.autoAddOrder ?? 0,
            viewMode: datagridField.viewMode ?? 'inline',
            drawerWidth: datagridField.drawerWidth ?? 40
          },
          children: undefined,
          parentId: null,
          tabId: null
        };

      case 'row':
        //  NOVO: Deserializar Row do JSON
        const rowField = field as any;
        return {
          id: nodeId,
          type: 'row',
          props: {
            ...baseProps,
            label: rowField.label || 'Linha',
            gap: rowField.gap || '12px',
            alignItems: rowField.alignItems || 'start',
            justifyContent: rowField.justifyContent || 'start',
            backgroundColor: rowField.backgroundColor,
            padding: rowField.padding || '8px',
            order: rowField.order ?? 0,
          },
          children: undefined,
          parentId: null,
          tabId: null
        };

      case 'image':
        return {
          id: nodeId,
          type: 'image',
          props: {
            ...baseProps,
            imageUrl: (field as any).imageUrl || 'https://via.placeholder.com/300x200',
            alt: (field as any).alt || 'Imagem',
            width: (field as any).width || 300,
            height: (field as any).height || 200,
            borderRadius: (field as any).borderRadius || 0,
            enableLightbox: (field as any).enableLightbox ?? true
          },
          children: undefined,
          parentId: null,
          tabId: null
        };

      default:
        // Para tipos desconhecidos, criar como text
        return {
          id: nodeId,
          type: 'text',
          props: baseProps,
          children: undefined,
          parentId: null,
          tabId: null
        };
    }
  }

  // Método auxiliar V5 para determinar se um componente é um componente webhook
  public isWebhookComponent(type: BuilderComponentType): boolean {
    const webhookTypes: BuilderComponentType[] = ['date', 'select', 'dropdown', 'querySelect', 'keyValue', 'keyValueObject', 'tag', 'search', 'divider', 'section'];
    return webhookTypes.includes(type);
  }

  // Método auxiliar para extrair propriedades de cor das props do componente
  private extractColorProperties(props: any): Record<string, string> {
    const colorProps: Record<string, string> = {};
    
    // Todas as propriedades de cor possíveis do nosso seletor de cores aprimorado
    const colorKeys = [
      // Cores do botão
      'backgroundColor', 'textColor',
      // Cores das abas
      'activeTabColor', 'activeTabTextColor', 'borderColor',
      'contentBackgroundColor', 'contentTextColor',
      // Cores da tabela
      'headerBackgroundColor', 'headerTextColor', 'rowBackgroundColor',
      'rowTextColor', 'alternateRowColor', 'hoverColor'
    ];

    colorKeys.forEach(key => {
      if (props[key]) {
        colorProps[key] = props[key];
      }
    });

    return colorProps;
  }

  // Método de atualização de nó para mudanças de propriedades
  updateNode(updatedNode: BuilderNode): void {
    const current = this.stateSubject.getValue();
    const nextNodes = current.nodes.map(n => n.id === updatedNode.id ? updatedNode : n);
    this.stateSubject.next({ ...current, nodes: nextNodes });
  }

  // Reseta propriedades do nó para padrões
  resetNodeProperties(nodeId: string): void {
    const current = this.stateSubject.getValue();
    const node = current.nodes.find(n => n.id === nodeId);
    if (!node) return;

    const defaultProps = this.createDefaultProps(node.type);
    const resetNode: BuilderNode = {
      ...node,
      props: defaultProps as BuilderProps,
    };

    this.updateNode(resetNode);
  }

  // Duplica nó
  duplicateNode(nodeId: string): void {
    const current = this.stateSubject.getValue();
    const node = current.nodes.find(n => n.id === nodeId);
    if (!node) return;

    const duplicatedNode: BuilderNode = {
      ...node,
      id: this.generateId(node.type + '-copy'),
      props: { ...node.props } as BuilderProps,
    };

    const nextNodes = [...current.nodes, duplicatedNode];
    this.stateSubject.next({ nodes: nextNodes, selectedId: duplicatedNode.id });
  }

  // Gerenciamento de estado para UI
  private savingState = new BehaviorSubject<boolean>(false);
  private unsavedChangesState = new BehaviorSubject<boolean>(false);
  private undoStack: BuilderScreenV2State[] = [];
  private redoStack: BuilderScreenV2State[] = [];

  isSaving$ = this.savingState.asObservable();
  hasUnsavedChanges$ = this.unsavedChangesState.asObservable();
  canUndo$ = new BehaviorSubject<boolean>(false);
  canRedo$ = new BehaviorSubject<boolean>(false);

  // Salva configuração
  saveConfiguration(): void {
    this.savingState.next(true);
    
    // Simula operação de salvamento
    setTimeout(() => {
      this.savingState.next(false);
      this.unsavedChangesState.next(false);      
    }, 1500);
  }

  // Cancela mudanças
  cancelChanges(): void {
    if (this.undoStack.length > 0) {
      const lastState = this.undoStack[0];
      this.stateSubject.next(lastState);
      this.unsavedChangesState.next(false);
    }
  }

  // Operação de desfazer
  undo(): void {
    if (this.undoStack.length > 0) {
      const currentState = this.stateSubject.getValue();
      const previousState = this.undoStack.pop()!;
      
      this.redoStack.push(currentState);
      this.stateSubject.next(previousState);
      
      this.canUndo$.next(this.undoStack.length > 0);
      this.canRedo$.next(this.redoStack.length > 0);
    }
  }

  // Operação de refazer
  redo(): void {
    if (this.redoStack.length > 0) {
      const currentState = this.stateSubject.getValue();
      const nextState = this.redoStack.pop()!;
      
      this.undoStack.push(currentState);
      this.stateSubject.next(nextState);
      
      this.canUndo$.next(this.undoStack.length > 0);
      this.canRedo$.next(this.redoStack.length > 0);
    }
  }

  // Exporta configuração
  exportConfiguration(): void {
    const jsonOutput = this.mapToJson(this.stateSubject.getValue().nodes);
    const blob = new Blob([JSON.stringify(jsonOutput, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `builder-configuration-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    
    URL.revokeObjectURL(url);
  }

  // Importa configuração
  importConfiguration(file: File): void {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target?.result as string);
        this.loadFromJson(json);        
      } catch (error) {        
      }
    };
    reader.readAsText(file);
  }

  /**
   * Carrega configuração a partir de um objeto JSON
   * Converte o JSON de volta para o formato BuilderNode e atualiza o estado
   */
  loadFromJson(jsonData: any, skipValidation = true): void {
    try {
      // Desabilitar validação ao carregar templates
      this.isLoadingTemplate = skipValidation;
      
      if (!jsonData) {
        this.isLoadingTemplate = false;
        return;
      }

      
      // Se o JSON contém a estrutura completa do output (tabs, panels, fields)
      if (jsonData.tabs && Array.isArray(jsonData.tabs)) {
      
        
        // Verifica se tem conteúdo válido
        const hasContent = (jsonData.tabs && jsonData.tabs.length > 0) ||
                          (jsonData.panels && jsonData.panels.length > 0) ||
                          (jsonData.fields && jsonData.fields.length > 0) ||
                          (jsonData.rows && jsonData.rows.length > 0);
        
        if (hasContent) {
      
          const nodes = this.convertJsonToNodes(jsonData);
          this.stateSubject.next({ nodes, selectedId: null });
      
        } else {
      
          this.stateSubject.next({ nodes: [], selectedId: null });
        }
      }
      // Se o JSON contém apenas um array de nodes
      else if (Array.isArray(jsonData)) {
        const validatedNodes = this.validateAndFixNodes(jsonData);
        this.stateSubject.next({ nodes: validatedNodes, selectedId: null });
        
      }
      // Se é um objeto que pode ser um node individual
      else if (jsonData.id && jsonData.type) {
        const validatedNode = this.validateAndFixNode(jsonData);
        if (validatedNode) {
          this.stateSubject.next({ nodes: [validatedNode], selectedId: null });
          
        }
      }
      // Se é um JSON vazio ou só com estrutura vazia
      else if (Object.keys(jsonData).length === 0 || 
               (jsonData.tabs && jsonData.tabs.length === 0 && 
                jsonData.panels && jsonData.panels.length === 0 && 
                jsonData.fields && jsonData.fields.length === 0)) {
        
        this.stateSubject.next({ nodes: [], selectedId: null });
      }
      // Se chegou até aqui, tenta uma conversão direta mais simples
      else if (typeof jsonData === 'object' && jsonData !== null) {
        
        
        // Tenta criar uma estrutura simples baseada no JSON
        try {
          const simpleNodes = this.createSimpleNodesFromJson(jsonData);
          if (simpleNodes.length > 0) {
            this.stateSubject.next({ nodes: simpleNodes, selectedId: null });
        
            return;
          }
        } catch (error) {
          console.error('Failed to create simple nodes:', error);
        }
        
      }
    } catch (error) {
      console.error('Error loading from JSON:', error);
    } finally {
      // Sempre restaurar flag de validação
      this.isLoadingTemplate = false;
    }
  }

  /**
   * Cria nodes simples a partir de um JSON genérico (fallback)
   */
  private createSimpleNodesFromJson(jsonData: any): BuilderNode[] {
    const nodes: BuilderNode[] = [];
    
    // Se tem propriedades que parecem ser de um componente individual
    if (jsonData.type || jsonData.label || jsonData.field) {
      const node: BuilderNode = {
        id: `simple-${Date.now()}`,
        type: jsonData.type || 'text',
        props: {
          label: jsonData.label || 'Campo',
          ...jsonData
        } as any,
        children: []
      };
      nodes.push(node);
    }
    
    return nodes;
  }

  /**
   * Converte estrutura JSON (tabs/panels/fields) de volta para BuilderNodes
   * IMPORTANTE: Este método deve preservar a estrutura original sem modificações
   * Agora suporta tanto BuilderJsonOutput quanto WebhookConfiguration
   */
  private convertJsonToNodes(jsonData: BuilderJsonOutput | WebhookConfiguration): BuilderNode[] {
    
    const nodes: BuilderNode[] = [];
    let nodeCounter = 1;

    // ✅ CORREÇÃO: Criar node de tabs SE existirem, preservando IDs originais
    if (jsonData.tabs && jsonData.tabs.length > 0) {
    
      // ✅ Gerar ID único para o container de tabs, mas preservar IDs das tabs individuais
      const tabsContainerId = this.generateId('tabs');
      
      // Pegar propriedades de estilo da primeira tab (todas as tabs compartilham o mesmo estilo no nó de tabs)
      const firstTab = jsonData.tabs[0] as any;
      
      const tabsNode: BuilderNode = {
        id: tabsContainerId, // Container precisa de ID único
        type: 'tabs',
        props: {
          items: jsonData.tabs.map(tab => ({
            id: tab.id, // ✅ PRESERVA O ID ORIGINAL DA TAB
            name: tab.name,
            icon: tab.icon || 'appstore',
            themeIcon: tab.themeIcon || 'outline',
            twotoneColor: tab.twotoneColor || '#1890ff',
            disabled: !!tab.disabled
          })),
          //  NOVO: Restaurar propriedades de estilo das abas
          backgroundColor: firstTab.backgroundColor,
          textColor: firstTab.textColor,
          activeTabColor: firstTab.activeTabColor,
          activeTabTextColor: firstTab.activeTabTextColor,
          borderColor: firstTab.borderColor,
          contentBackgroundColor: firstTab.contentBackgroundColor,
          contentTextColor: firstTab.contentTextColor,
          hoverTabColor: firstTab.hoverTabColor,
          //  NOVO: Restaurar propriedades do container principal
          tabsContainerBackgroundColor: firstTab.tabsContainerBackgroundColor,
          tabsContainerBorderColor: firstTab.tabsContainerBorderColor,
          tabsContainerBorderRadius: firstTab.tabsContainerBorderRadius,
          tabsContainerPadding: firstTab.tabsContainerPadding,
          //  NOVO: Restaurar propriedades do wrapper externo
          wrapperBackgroundColor: firstTab.wrapperBackgroundColor,
          wrapperBorderColor: firstTab.wrapperBorderColor,
          wrapperBorderRadius: firstTab.wrapperBorderRadius,
          wrapperPadding: firstTab.wrapperPadding
        } as any,
        children: []
      };

      // Barra de Contexto: restaurar contextFields após criação do node
      const ctxFields = (jsonData as any).contextFields;
      if (ctxFields && Array.isArray(ctxFields) && ctxFields.length > 0) {
        (tabsNode.props as any).contextFields = ctxFields;
        console.log('[ContextStrip] Restaurados contextFields do JSON:', ctxFields);
      }

      nodes.push(tabsNode);

    }

    // ✅ CORREÇÃO: Processar panels e preservar IDs originais
    if (jsonData.panels && jsonData.panels.length > 0) {

      // Encontrar o node tabs para usar como parent
      const tabsNode = nodes.find(n => n.type === 'tabs');
      const tabsParentId = tabsNode?.id || null;
      
      jsonData.panels.forEach(panel => {
        const panelNode: BuilderNode = {
          id: panel.id, // ✅ PRESERVA O ID ORIGINAL DO PANEL
          type: 'panel',
          props: {
            name: panel.name,
            active: panel.active !== undefined ? panel.active : false,
            disabled: panel.disabled !== undefined ? panel.disabled : false,
            order: (panel as any).order ?? 0, // ✅ CORRIGIDO: usar ?? para aceitar 0 como valor válido
            //  NOVO: Restaurar propriedades de colapso e estilo do JSON (legacy)
            collapsible: (panel as any).collapsible !== undefined ? (panel as any).collapsible : true,
            collapsed: (panel as any).collapsed !== undefined ? (panel as any).collapsed : !panel.active,
            backgroundColor: (panel as any).backgroundColor,
            textColor: (panel as any).textColor,
            //  NOVO: Restaurar propriedades de estilo específicas do painel
            panelBorderColor: (panel as any).panelBorderColor,
            panelHeaderBackgroundColor: (panel as any).panelHeaderBackgroundColor,
            panelHeaderTextColor: (panel as any).panelHeaderTextColor,
            panelContentBackgroundColor: (panel as any).panelContentBackgroundColor,
            //  NOVO: Restaurar propriedades de posicionamento na grade
            colSpan: (panel as any).colSpan ?? 24,
            rowSpan: (panel as any).rowSpan ?? 1,
            col: (panel as any).col ?? 0,
            row: (panel as any).row ?? 0
          } as any,
          children: [],
          parentId: tabsParentId, // Panel deve ser filho do tabs
          tabId: panel.tab // ✅ PRESERVA O ID DA TAB REFERENCIADA
        };
        nodes.push(panelNode);
        
      });
    }

    //  NOVO: Processar array separado de Rows (se existir)
    //  PASSO 1: Criar mapeamento de IDs (panels e rows)
    const panelIdToNodeId = new Map<string, string>();
    
    // Adicionar panels reais ao mapeamento
    nodes.filter(n => n.type === 'panel').forEach(panel => {
      panelIdToNodeId.set(panel.id, panel.id);
    });
    
    //  PASSO 2: Processar Rows do array separado (jsonData.rows)
    
    
    if ((jsonData as any).rows && (jsonData as any).rows.length > 0) {
      const rowsData = (jsonData as any).rows;
    
      
      rowsData.forEach((rowField: any, idx: number) => {
        
        const fieldNode = this.convertWebhookFieldToNode(rowField as WebhookField, nodeCounter++);
        
        if (fieldNode) {
          // ✅ CORRIGIDO: Usar dataPath como ID estável para o Row.
          // Antes, cada save/load gerava um novo ID envolvendo o anterior,
          // fazendo com que o ID crescesse infinitamente (field_row-field_row-field_row-...).
          // Agora usamos o dataPath (ex: "row-k7dade-mklkoaq3") que é curto e estável.
          const rowId = rowField.dataPath || rowField.field || `row-${this.generateId('field')}`;
          fieldNode.id = rowId;
          
          // Atualizar props com o ID limpo (evita que o field longo persista)
          (fieldNode.props as any).field = rowId;
          (fieldNode.props as any).fieldName = rowId;
          
          // Mapear Row para panel (parentId) e tab
          fieldNode.parentId = rowField.panel; // Parent do Row (panel ou null)
          
          // Encontrar a tab do panel
          const panel = jsonData.panels?.find(p => p.id === rowField.panel);
          fieldNode.tabId = panel?.tab || null;
          
          //  IMPORTANTE: Mapear o field original (possivelmente longo) → novo ID curto
          // Isso permite que campos filhos com panel = campo longo encontrem o Row correto
          if (rowField.field && rowField.field !== rowId) {
            panelIdToNodeId.set(rowField.field, rowId);
          }
          // Também mapear o dataPath → ID (para referências limpas)
          panelIdToNodeId.set(rowId, rowId);
          
          nodes.push(fieldNode);
          
        }
      });
    }
    
    // ✅ PASSO 3: Processar fields normais
    if (jsonData.fields && jsonData.fields.length > 0) {
      
      
      // Todos os fields são regulares agora (Rows estão em array separado)
      jsonData.fields.forEach((field, idx) => {
        const fieldNode = this.convertWebhookFieldToNode(field as WebhookField, nodeCounter++);
        if (fieldNode) {
      
          if (field.field) {
            fieldNode.id = `field_${field.type}-${field.field}-${this.generateId('field')}`;
      
            (fieldNode.props as any).field = field.field;
            (fieldNode.props as any).fieldName = field.field;
          }
          
          //  CORREÇÃO: Mapear field para parent correto (pode ser panel OU Row)
          // Se field.panel está no mapeamento, é filho de um Row
          if (panelIdToNodeId.has(field.panel)) {
            const rowNodeId = panelIdToNodeId.get(field.panel)!;
            const rowNode = nodes.find(n => n.id === rowNodeId);
            
            if (rowNode) {
              fieldNode.parentId = rowNodeId; // Parent é o Row
              fieldNode.tabId = rowNode.tabId; // Herdar tab do Row
              
            } else {
              // Fallback: usar panel como parent
              fieldNode.parentId = field.panel;
              const panel = jsonData.panels?.find(p => p.id === field.panel);
              fieldNode.tabId = panel?.tab || null;
            }
          } else {
            // Parent é um panel normal
            fieldNode.parentId = field.panel;
            const panel = jsonData.panels?.find(p => p.id === field.panel);
            fieldNode.tabId = panel?.tab || null;
          }
          
          nodes.push(fieldNode);
          
        }
      });
    }

    
    return nodes;
  }

  /**
   * Método antigo mantido para compatibilidade - DEPRECIADO
   */
  private convertJsonToNodesOld(jsonData: BuilderJsonOutput): BuilderNode[] {
    
    const nodes: BuilderNode[] = [];
    let nodeCounter = 1;

    // Processa tabs
    if (jsonData.tabs && jsonData.tabs.length > 0) {
      jsonData.tabs.forEach(tab => {
        const tabNode: BuilderNode = {
          id: `tab-${nodeCounter++}`,
          type: 'tabs',
          props: {
            label: tab.name,
            icon: tab.icon,
            disabled: tab.disabled
          } as any,
          children: []
        };

        // Processa panels dentro da tab
        if (jsonData.panels && jsonData.panels.length > 0) {
          const tabPanels = jsonData.panels.filter(panel => panel.tab === tab.id);
          
          
          tabPanels.forEach(panel => {
            const panelNode: BuilderNode = {
              id: `panel-${nodeCounter++}`,
              type: 'section',
              props: {
                label: panel.name,
                disabled: panel.disabled
              } as any,
              children: []
            };

            // Processa fields dentro do panel
            if (jsonData.fields && jsonData.fields.length > 0) {
              const panelFields = jsonData.fields.filter(field => field.panel === panel.id);
              
              
              panelFields.forEach(field => {
                const fieldNode = this.convertFieldToNode(field, nodeCounter++);
                if (fieldNode) {
                  panelNode.children?.push(fieldNode);
                  
                }
              });
            }

            // Só adiciona o panel se ele tiver fields ou se for explicitamente necessário
            if (panelNode.children && panelNode.children.length > 0) {
              tabNode.children?.push(panelNode);
              
            } else {
              
            }
          });
        }

        // Só adiciona a tab se ela tiver panels ou se for explicitamente necessária
        if (tabNode.children && tabNode.children.length > 0) {
          nodes.push(tabNode);
          
        } 
      });
    }

    // Se não criamos nenhum node através da estrutura de tabs/panels,
    // mas temos fields, vamos processá-los diretamente
    if (nodes.length === 0 && jsonData.fields && jsonData.fields.length > 0) {
      
      
      jsonData.fields.forEach(field => {
        const fieldNode = this.convertFieldToNode(field, nodeCounter++);
        if (fieldNode) {
          nodes.push(fieldNode);
      
        }
      });
    }

    
    return nodes;
  }

  /**
   * Converte um campo do JSON para um BuilderNode
   * IMPORTANTE: Preservar TODAS as propriedades do field original
   */
  private convertFieldToNode(field: OutputField, nodeId: number): BuilderNode | null {
    
    
    const baseNode = {
      id: `field-${nodeId}`,
      children: []
    };

    // Extrair todas as propriedades do field, exceto as de controle
    const { field: fieldName, panel, col, row, colSpan, required, colors, type, ...fieldProps } = field as any;

    switch (field.type) {
      case 'text':
        return {
          ...baseNode,
          type: 'text',
          props: {
            label: field.label,
            placeholder: field.placeholder,
            colSpan: field.colSpan || 24,
            required: field.required || false,
            ...fieldProps // Preservar propriedades extras
          } as any
        };

      case 'textarea':
        return {
          ...baseNode,
          type: 'textarea',
          props: {
            label: field.label,
            placeholder: field.placeholder,
            colSpan: field.colSpan || 24,
            required: field.required || false,
            ...fieldProps
          } as any
        };

      case 'number':
        return {
          ...baseNode,
          type: 'number',
          props: {
            label: field.label,
            placeholder: field.placeholder,
            colSpan: field.colSpan || 24,
            required: field.required || false,
            ...fieldProps
          } as any
        };

      case 'switch':
        return {
          ...baseNode,
          type: 'switch',
          props: {
            label: field.label,
            colSpan: field.colSpan || 24,
            required: field.required || false,
            ...fieldProps
          } as any
        };

      case 'select':
        return {
          ...baseNode,
          type: 'select',
          props: {
            label: field.label,
            placeholder: field.placeholder,
            colSpan: field.colSpan || 24,
            required: field.required || false,
            ...fieldProps
          } as any
        };

      case 'date':
        return {
          ...baseNode,
          type: 'date',
          props: {
            label: field.label,
            placeholder: field.placeholder,
            colSpan: field.colSpan || 24,
            required: field.required || false,
            ...fieldProps
          } as any
        };

      case 'button':
        return {
          ...baseNode,
          type: 'button',
          props: {
            label: field.label,
            colSpan: field.colSpan || 24,
            required: field.required || false,
            ...fieldProps // Preservar todas as propriedades do button (text, buttonType, size, actionType, disabled, loading, etc.)
          } as any
        };

      case 'section':
        return {
          ...baseNode,
          type: 'section',
          props: {
            label: field.label,
            title: field.label,
            colSpan: field.colSpan || 24,
            ...fieldProps
          } as any
        };

      case 'divider':
        return {
          ...baseNode,
          type: 'divider',
          props: {
            label: field.label || 'Divisor',
            colSpan: field.colSpan || 24,
            ...fieldProps
          } as any
        };

      case 'image':
        return {
          ...baseNode,
          type: 'image',
          props: {
            label: field.label || 'Imagem',
            colSpan: field.colSpan || 24,
            imageUrl: (field as any).imageUrl || 'https://via.placeholder.com/300x200',
            alt: (field as any).alt || 'Imagem',
            width: (field as any).width || 300,
            height: (field as any).height || 200,
            borderRadius: (field as any).borderRadius || 0,
            enableLightbox: (field as any).enableLightbox ?? true,
            ...fieldProps
          } as any
        };

      default:
        // Para tipos desconhecidos, criar um campo text como fallback
        return {
          ...baseNode,
          type: 'text',
          props: {
            label: field.label || 'Campo',
            placeholder: field.placeholder || 'Digite aqui...',
            colSpan: field.colSpan || 24,
            required: field.required || false,
            ...fieldProps
          } as any
        };
    }
  }

  /**
   * Valida e corrige array de nodes
   */
  private validateAndFixNodes(nodes: any[]): BuilderNode[] {
    return nodes
      .map(node => this.validateAndFixNode(node))
      .filter(node => node !== null) as BuilderNode[];
  }

  /**
   * Valida e corrige um node individual
   */
  private validateAndFixNode(node: any): BuilderNode | null {
    if (!node || typeof node !== 'object') {
      return null;
    }

    // Propriedades obrigatórias
    const id = node.id || `node-${Date.now()}-${Math.random()}`;
    const type = node.type;
    
    if (!type) {
      return null;
    }

    // Validação de tipo
    const validTypes: BuilderComponentType[] = [
      'text', 'textarea', 'number', 'switch', 'select', 'date', 
      'tabs', 'panel', 'section', 'divider', 'button', 'table', 'keyValue', 
      'keyValueObject', 'tag', 'search', 'querySelect', 'datagrid',
      'row', 'dropdown', 'image'  // ✅ CORRIGIDO: tipos faltantes que eram descartados
    ];

    if (!validTypes.includes(type)) {
      return null;
    }

    return {
      id,
      type: type as BuilderComponentType,
      props: node.props || {},
      children: Array.isArray(node.children) 
        ? this.validateAndFixNodes(node.children)
        : [],
      parentId: node.parentId || null, // Preservar parentId
      tabId: node.tabId || null        // Preservar tabId
    };
  }

  // Rastreia mudanças de estado para desfazer/refazer
  private trackStateChange(): void {
    this.state$.subscribe(state => {
      // Empurra estado atual para pilha de desfazer (limita a 50 estados)
      if (this.undoStack.length >= 50) {
        this.undoStack.shift();
      }
      
      this.unsavedChangesState.next(true);
      this.canUndo$.next(this.undoStack.length > 0);
      
      // Limpa pilha de refazer quando novas mudanças são feitas
      this.redoStack = [];
      this.canRedo$.next(false);
    });
  }

  // ========== JSON Preview Fix Methods ==========

  /**
   * Aplica configuração JSON completa ao canvas
   * Task 1.1: Método principal para aplicar mudanças do JSON editor
   * Agora suporta tanto BuilderJsonOutput quanto WebhookConfiguration
   */
  applyJsonConfiguration(jsonData: BuilderJsonOutput | WebhookConfiguration): JsonApplyResult {
    try {
      

      // Validar estrutura JSON
      const validation = this.validateJsonConfiguration(jsonData);
      if (!validation.isValid) {
        return {
          success: false,
          errors: validation.errors,
          warnings: [],
          appliedNodes: 0
        };
      }

      // Preservar seleção atual se possível
      const currentState = this.stateSubject.getValue();
      const currentSelectedId = currentState.selectedId;

      // Converter JSON para nodes usando método existente
      const newNodes = this.convertJsonToNodes(jsonData);
      
      if (!newNodes || newNodes.length === 0) {
        // Diagnóstico mais detalhado do problema
        let detailedMessage = 'Nenhum componente válido foi encontrado no JSON.';
        
        if (!jsonData.fields || jsonData.fields.length === 0) {
          detailedMessage += ' Nenhum field foi definido.';
        } else {
          detailedMessage += ` ${jsonData.fields.length} field(s) encontrado(s) mas não foi possível convertê-los.`;
        }

        if (!jsonData.tabs || jsonData.tabs.length === 0) {
          detailedMessage += ' Nenhuma tab foi definida.';
        }

        if (!jsonData.panels || jsonData.panels.length === 0) {
          detailedMessage += ' Nenhum panel foi definido.';
        }

        return {
          success: false,
          errors: [{
            type: 'component',
            message: detailedMessage
          }],
          warnings: [],
          appliedNodes: 0
        };
      }

      // Validar nodes convertidos
      const validatedNodes = this.validateAndFixNodes(newNodes);

      // Tentar preservar seleção se o ID ainda existir
      let newSelectedId: string | null = null;
      if (currentSelectedId && validatedNodes.find(n => n.id === currentSelectedId)) {
        newSelectedId = currentSelectedId;
      } else if (validatedNodes.length > 0) {
        // Selecionar o primeiro node se não conseguir preservar
        newSelectedId = validatedNodes[0].id;
      }

      // Atualizar state
      this.stateSubject.next({
        nodes: validatedNodes,
        selectedId: newSelectedId
      });

      // Aplicar controle inicial de abas para switches com tabControl habilitado
      this.applyInitialTabControls(validatedNodes);

      

      return {
        success: true,
        errors: [],
        warnings: validation.warnings || [],
        appliedNodes: validatedNodes.length
      };

    } catch (error: any) {
      console.error('Error applying JSON configuration:', error);
      return {
        success: false,
        errors: [{
          type: 'syntax',
          message: `Erro ao aplicar configuração: ${error.message || error}`
        }],
        warnings: [],
        appliedNodes: 0
      };
    }
  }

  /**
   * Valida configuração JSON antes da aplicação
   * Task 1.3: Método de validação prévia usando JsonValidationService
   * Agora suporta tanto BuilderJsonOutput quanto WebhookConfiguration
   */
  validateJsonConfiguration(jsonData: BuilderJsonOutput | WebhookConfiguration): JsonValidationResult {
    try {
      // Usar o JsonValidationService para validação avançada
      return this.jsonValidationService.validateJsonSchema(jsonData as any);
    } catch (error: any) {
      return {
        isValid: false,
        errors: [{
          type: 'syntax',
          message: `Erro na validação JSON: ${error.message || error}`
        }],
        warnings: []
      };
    }
  }

  /**
   * Valida sintaxe JSON de uma string antes do parse
   */
  validateJsonSyntax(jsonString: string): JsonValidationResult {
    return this.jsonValidationService.validateJsonSyntax(jsonString);
  }

  /**
   * Método auxiliar para limpar o canvas antes de aplicar nova configuração
   */
  clearCanvas(): void {
    this.stateSubject.next({
      nodes: [],
      selectedId: null
    });
    
    // Limpar cache de abas ativas
    Object.keys(this.activeTabByTabsId).forEach(key => {
      delete this.activeTabByTabsId[key];
    });
  }

  /**
   * Método para aplicar JSON com opção de limpar canvas
   */
  applyJsonConfigurationWithClear(jsonData: BuilderJsonOutput | WebhookConfiguration, clearFirst: boolean = true): JsonApplyResult {
    if (clearFirst) {
      this.clearCanvas();
    }
    return this.applyJsonConfiguration(jsonData);
  }

  /**
   * Coleta valores de TODOS os campos da tela (preenchidos ou não)
   * Retorna objeto no formato { CAMPO: valor, ... }
   * Campos vazios retornam null/''/''/0 dependendo do tipo
   */
  getFormData(): Record<string, any> {
    const formData: Record<string, any> = {};
    const state = this.stateSubject.getValue();
    
    // Função recursiva para processar nós e seus filhos
    const processNode = (node: BuilderNode) => {
      // Apenas campos de input (input, textarea, select, etc)
      if (this.isFieldComponent(node.type)) {
        const fieldName = this.getFieldName(node);
        const fieldValue = this.getFieldValue(node);
        
        if (fieldName) {
          // ✅ Incluir TODOS os campos, mesmo vazios
          formData[fieldName.toUpperCase()] = fieldValue ?? this.getDefaultValueForType(node.type);
        }
      }
      
      // Processar filhos recursivamente (tabs, panels, etc)
      if (node.children && node.children.length > 0) {
        node.children.forEach(child => processNode(child));
      }
    };
    
    // Processar todos os nós (incluindo nós aninhados)
    state.nodes.forEach(node => processNode(node));
    
    
    return formData;
  }
  
  /**
   * Retorna valor padrão para um tipo de campo quando vazio
   */
  private getDefaultValueForType(type: string): any {
    switch (type) {
      case 'switch':
        return false;
      case 'number':
        return 0;
      case 'tag':
        return [];
      case 'text':
      case 'textarea':
      case 'select':
      case 'dropdown':
      case 'querySelect':
      case 'search':
      case 'date':
      default:
        return null;
    }
  }

  private isFieldComponent(type: string): boolean {
    return ['text', 'textarea', 'select', 'dropdown', 'number', 'switch', 
            'date', 'querySelect', 'search', 'tag'].includes(type);
  }

  private getFieldName(node: BuilderNode): string {
    const props = node.props as any;
    
    // Prioridade: field > fieldName > label > ID
    // 'field' vem do JSON original (ex: "codeproduct", "usaMeli")
    return props.field || 
           props.fieldName || 
           props.label || 
           node.id;
  }

  private getFieldValue(node: BuilderNode): any {
    // Cada tipo de campo pode armazenar o valor de forma diferente
    const props = node.props as any;
    
    switch (node.type) {
      case 'switch':
        return props.defaultValue !== undefined ? props.defaultValue : false;
      case 'number':
        return props.value !== undefined ? props.value : (props.defaultValue || 0);
      case 'select':
      case 'dropdown':
      case 'querySelect':
        return props.value || props.selectedValue || props.defaultValue;
      case 'date':
        return props.value || props.selectedDate || props.defaultValue;
      case 'search':
        return props.value || props.selectedValue;
      case 'tag':
        return props.value || props.tags || [];
      case 'text':
      case 'textarea':
      default:
        return props.value !== undefined ? props.value : (props.defaultValue || '');
    }
  }

  /**
   * Aplica controle inicial de abas para switches com tabControl habilitado
   */
  private applyInitialTabControls(nodes: ReadonlyArray<BuilderNode>): void {
    // Aguardar um pequeno delay para garantir que o DOM foi renderizado
    setTimeout(() => {
      nodes.forEach(node => {
        if (node.type === 'switch') {
          const tabControl = (node.props as any)?.tabControl;
          if (tabControl?.enabled) {
            const defaultValue = (node.props as any)?.defaultValue || false;
            this.tabControlService.applyInitialTabControl(defaultValue, tabControl);
          }
        }
      });
    }, 100);
  }
}


